import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    model,
    signal,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    ControlContainer,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormGroupDirective,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';

import { AccordionModule } from 'primeng/accordion';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { InvoiceCardCustomerComponent } from '../invoice-card-customer/invoice-card-customer/invoice-card-customer.component';
import {
    AddressType,
    AddressTypeFormGroupSelector,
    AddressTypeQuery,
    BranchGraphQLSchemaActions,
    BranchGraphQLSchemaSelectors,
    BranchTypeQuery,
    CityTypeQuery,
    ContactDetailType,
    ContactDetailTypeFormGroup,
    ContactDetailTypeFormGroupSelector,
    ContactDetailTypeQuery,
    CourierTypeQuery,
    CustomerGraphQLSchemaActions,
    CustomerGraphQLSchemaSelectors,
    CustomerType,
    CustomerTypeFormGroupSelector,
    CustomerTypeQuery,
    DCLGarageUseTypeQuery,
    DCLParkingCarWashUseTypeQuery,
    DCLRentalUseTypeQuery,
    DCLResponseTypeFormGroupSelector,
    DCLResponseTypeQuery,
    DigitalClientCardType,
    DigitalClientCardTypeQuery,
    DiscountCategoryTypeQuery,
    FinancialDetailTypeQuery,
    FirstNameTypeQuery,
    InfoNoteTypeQuery,
    InvoiceDeliveryNoteHeaderTypeQuery,
    InvoiceDetailGraphQLSchemaActions,
    InvoiceDetailGraphQLSchemaSelectors,
    InvoiceDetailType,
    InvoiceDetailTypeFormGroup,
    InvoiceDetailTypeFormGroupModel,
    InvoiceDetailTypeFormGroupSelector,
    InvoiceDetailTypeQuery,
    InvoiceEntitySubjectInfoTypeQuery,
    InvoiceGraphQLSchemaActions,
    InvoiceGraphQLSchemaSelectors,
    InvoiceIncomeClassificationType,
    InvoiceIncomeClassificationTypeFormGroup,
    InvoiceIncomeClassificationTypeFormGroupSelector,
    InvoiceIncomeClassificationTypeQuery,
    InvoiceNoteType,
    InvoiceNoteTypeFormGroupSelector,
    InvoiceNoteTypeQuery,
    InvoicePartyType,
    InvoicePartyTypeFormGroup,
    InvoicePartyTypeFormGroupSelector,
    InvoicePartyTypeQuery,
    InvoicePaymentType,
    InvoicePaymentTypeFormGroupSelector,
    InvoicePaymentTypeQuery,
    InvoiceSummaryType,
    InvoiceSummaryTypeFormGroupSelector,
    InvoiceSummaryTypeQuery,
    InvoiceTaxGraphQLSchemaActions,
    InvoiceTaxType,
    InvoiceTaxTypeFormGroupSelector,
    InvoiceTaxTypeQuery,
    InvoiceType,
    InvoiceTypeFormGroup,
    InvoiceTypeFormGroupSelector,
    InvoiceTypeQuery,
    OccupationActivityTypeQuery,
    POSTerminal,
    POSTerminalErrorDataTypeFormGroupSelector,
    POSTerminalErrorDataTypeQuery,
    POSTerminalGraphQLSchemaActions,
    POSTerminalGraphQLSchemaSelectors,
    POSTerminalResponseType,
    POSTerminalResponseTypeFormGroupSelector,
    POSTerminalResponseTypeQuery,
    POSTerminalServiceDataTypeFormGroupSelector,
    POSTerminalServiceDataTypeQuery,
    POSTerminalTransactionDataTypeFormGroupSelector,
    POSTerminalTransactionDataTypeQuery,
    POSTerminalTransactionType,
    POSTerminalTransactionTypeFormGroupSelector,
    POSTerminalTransactionTypeQuery,
    POSTerminalTypeQuery,
    ProductInventoryInfoTypeFormGroup,
    ProductInventoryInfoTypeQuery,
    ProductInvoicingInfoTypeFormGroup,
    ProductInvoicingInfoTypeQuery,
    ProductPackageGraphQLSchemaActions,
    ProductPackageGraphQLSchemaSelectors,
    ProductPackageType,
    ProductPackageTypeFormGroupSelector,
    ProductPackageTypeQuery,
    ProductToDiscountTypeQuery,
    ProductUnitType,
    ProductUnitTypeGraphQLSchemaActions,
    ProductUnitTypeGraphQLSchemaSelectors,
    ProductUnitTypeType,
    ProductUnitTypeTypeFormGroup,
    ProductUnitTypeTypeFormGroupSelector,
    ProductUnitTypeTypeQuery,
    ProviderResponseErrorTypeFormGroupSelector,
    ProviderResponseErrorTypeQuery,
    ProviderResponseTypeFormGroupSelector,
    ProviderResponseTypeQuery,
    ProviderSignatureMoreDataTypeFormGroupSelector,
    ProviderSignatureMoreDataTypeQuery,
    ProviderSignatureTypeFormGroupSelector,
    ProviderSignatureTypeQuery,
    ResponseTypeErrorTypeFormGroupSelector,
    ResponseTypeErrorTypeQuery,
    SignatureInvoiceResponseTypeFormGroupSelector,
    SignatureInvoiceResponseTypeQuery,
    SupplierGraphQLSchemaActions,
    SupplierGraphQLSchemaSelectors,
    SupplierType,
    SupplierTypeQuery,
    TAXOfficeTypeFormGroupSelector,
    TAXOfficeTypeQuery,
    TraderProfitMarginTypeQuery,
    UsersActionsFormGroup,
    UsersActionsTypeQuery,
    VATCategoryGraphQLSchemaActions,
    VATCategoryGraphQLSchemaSelectors,
    VATCategoryType,
    VATCategoryTypeFormGroupSelector,
    VATCategoryTypeQuery
} from '@shared/zigra-schemas/zigra-schema.autogenerated.models';
import { InvoiceCardHeaderInfoComponent } from '../invoice-card-header-info/invoice-card-header-info.component';
import { InvoiceCardHeaderContactsComponent } from '../invoice-card-header-contacts/invoice-card-header-contacts.component';
import { TableModule } from 'primeng/table';
import { ZProductSelectComponent } from '../../../../../shared/components/bussiness-components/z-product-select/z-product-select.component';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { InvoiceOptions } from '../../entities/invoice-card.options';
import { ZFloatLabelComponent } from '@shared/components/form-components/z-float-label/z-float-label.component';
import { ZFloatLabelNumberComponent } from '@shared/components/form-components/z-float-label-number/z-float-label-number.component';
import { ZFloatLabelCurrencyComponent } from '@shared/components/form-components/z-float-label-currency/z-float-label-currency.component';
import { ZFloatLabelPercentComponent } from '@shared/components/form-components/z-float-label-percent/z-float-label-percent.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    concatMap,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    of,
    timer
} from 'rxjs';
import { ZFloatLabelNotesComponent } from '@shared/components/form-components/z-float-label-notes/z-float-label-notes.component';
import { NotesFilterPipe } from '../../../../../shared/pipes/notes-filter.pipe';
import { TaxFilterPipe } from '../../../../../shared/pipes/tax-filter.pipe';
import { Store } from '@ngxs/store';
import {
    QueryFilterGroup,
    QueryFilter,
    FetchQueryParams
} from 'zigra-ng-framework';
import { LoaderService } from '@shared/services/loader.service';
import { ZProductMeterUnitSelectComponent } from '../../../../../shared/components/bussiness-components/z-product-meter-unit-select/z-product-meter-unit-select.component';
import { ZVatCategoriesSelectComponent } from '@shared/components/bussiness-components/z-vat-categories-select/z-vat-categories-select.component';
import { EntityFormModeType } from '@shared/types/entity-form-mode.type';
import { NoAction } from '@shared/zigra-schemas/custom/no-action';
import { ZDatePickerComponent } from '@shared/components/form-components/z-date-picker/z-date-picker.component';
import { FieldsetModule } from 'primeng/fieldset';
import { ZCashRegisterCashComponent } from '../../../../../shared/components/form-components/z-cash-register-cash/z-cash-register-cash.component';
import {
    IInvoiceDetailKindType,
    ZInvoiceDetailKindSelectComponent
} from '@shared/components/bussiness-components/z-invoice-kind-select/z-invoice-detail-kind-select/z-invoice-detail-kind-select.component';
import { Button } from 'primeng/button';
import { ZInvoiceUseRetailPricesSelectComponent } from '@shared/components/bussiness-components/z-invoice-use-retail-prices-select/z-invoice-use-retail-prices-select/z-invoice-use-retail-prices-select.component';
import { ConfirmationService } from 'primeng/api';
import { ZCurrencyPipe } from '../../../../../shared/pipes/ZCurrency.pipe';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';

@Component({
    selector: 'zion-invoice-card',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        AccordionModule,
        AvatarModule,
        BadgeModule,
        InvoiceCardCustomerComponent,
        InvoiceCardHeaderInfoComponent,
        InvoiceCardHeaderContactsComponent,
        TableModule,
        ZProductSelectComponent,
        ZFloatLabelComponent,
        ZFloatLabelNumberComponent,
        ZFloatLabelCurrencyComponent,
        ZFloatLabelPercentComponent,
        ZFloatLabelNotesComponent,
        TaxFilterPipe,
        ZProductMeterUnitSelectComponent,
        ZVatCategoriesSelectComponent,
        FieldsetModule,
        ZCashRegisterCashComponent,
        ZInvoiceDetailKindSelectComponent,
        Button,
        ZCurrencyPipe,
        InputTextModule,
        TabsModule,
        ToastModule
    ],
    providers: [FormGroupDirective],
    templateUrl: './invoice-card.component.html',
    styleUrl: './invoice-card.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceCardComponent {
    @ViewChild('invoiceCardCustomer')
    invoiceCardCustomer?: InvoiceCardCustomerComponent;
    dialogConfigDataOptions: DynamicDialogConfig<InvoiceOptions> = inject(
        DynamicDialogConfig<InvoiceOptions>
    );

    // fb = inject(FormBuilder);
    // myForm: FormGroup = this.fb.group({
    //     name: ['', Validators.required],
    //     email: ['', [Validators.required, Validators.email]],
    //     address: this.fb.group({
    //         street: ['', Validators.required],
    //         city: ['', Validators.required],
    //         country: ['', Validators.required]
    //     })
    // });

    cd = inject(ChangeDetectorRef);
    store = inject(Store);
    loaderService = inject(LoaderService);
    confirmationService = inject(ConfirmationService);

    taxesMap = signal<Map<string, InvoiceTaxType>>(
        new Map<string, InvoiceTaxType>()
    );
    selectedCustomer = signal<CustomerType | undefined>(undefined);
    selectedSupplier = signal<SupplierType | undefined>(undefined);
    vatCategories = signal<VATCategoryType[] | undefined>(undefined);
    productsUnitsTypes = signal<ProductUnitTypeType[] | undefined>(undefined);
    selectedInvoiceTab = signal<'services' | 'products' | 'none' | number>(
        'products'
    );

    UseWithDCLInvoiceTypeCode = signal<boolean>(false);
    selectedRowIndex?: number;

    // /**
    //  * The pointer to active invoiceFormGroup
    //  */
    // invoiceFormGroup = signal<InvoiceTypeFormGroup | undefined>(undefined);
    invoiceFormGroups = signal<InvoiceTypeFormGroup[]>([]);
    //invoice?: InvoiceType;
    invoices: InvoiceType[] = [];

    columnAvailableQuantity: boolean = false;
    // Ποσότητα Συσκευασίας
    packageQuantity: boolean = false;
    renderInvoice: boolean = false;
    sourceModal?: boolean;

    dialogHeight = signal<string | undefined>('600px');
    dclCard = signal<DigitalClientCardType | undefined>(undefined);

    /**
     *
     */
    constructor() {
        // this.invoiceFormGroup()?.controls.Summaries?.controls?.[0]?.controls
        //     ?.TotalGrossValue;

        const invoiceDialogOptionsData = this.dialogConfigDataOptions
            .data as InvoiceOptions;

        invoiceDialogOptionsData.selectedInvoiceTab =
            invoiceDialogOptionsData.selectedInvoiceTab ??
            signal<number | 'services' | 'products' | 'none'>('products');

        this.dclCard.set(invoiceDialogOptionsData.dclCard);

        invoiceDialogOptionsData.onCompleteCorrelate = (
            options: InvoiceOptions,
            invoice: InvoiceType | undefined
        ) => {
            this.fetchData(true).subscribe();
        };

        invoiceDialogOptionsData.onRefreshInvoices = (
            options: InvoiceOptions,
            invoices: (InvoiceType | undefined)[]
        ) => {
            // πρέπει να ενημέρωσουμε το invoice που κάναμε save για να πάρει το _id του και να φορτώσει τις αλλαγές στην περίπτωση νέου παραστατικού!!!
            if (this.invoices?.[0] != undefined)
                this.invoices[0]._id = invoices[0]?._id;
            if (this.invoices?.[1] != undefined)
                this.invoices[1]._id = invoices[1]?._id;

            return this.fetchData(true);
        };

        // Εδώ έρχεται και από την κλήση SendToAADEConfirmed του footer
        invoiceDialogOptionsData.onSaved = (invoice, selectedInvoiceTab) => {
            // πρέπει να ενημέρωσουμε το invoice που κάναμε save για να πάρει το _id του και να φορτώσει τις αλλαγές στην περίπτωση νέου παραστατικού!!!
            if (selectedInvoiceTab == 'services' && this.invoices[0])
                this.invoices[0] = invoice;
            if (selectedInvoiceTab == 'products' && this.invoices[1])
                this.invoices[1] = invoice;

            this.fetchData(true).subscribe();
        };

        invoiceDialogOptionsData.onCorrelationCompleted = (
            invoice,
            selectedInvoiceTab
        ) => {
            this.fetchData(true).subscribe();
        };

        this.sourceModal = invoiceDialogOptionsData.sourceModal;

        this.invoices = []; //dialogOptions.invoices ?? [];

        const defaultInvoice = {
            TypeCode: invoiceDialogOptionsData.DCLInvoiceTypeCode, // '1.1',

            IssueDate: new Date(),
            DispatchDate:
                invoiceDialogOptionsData.DCLInvoiceTypeCode == '9.3' ||
                invoiceDialogOptionsData.DCLInvoiceTypeCode == '1.1-9.3'
                    ? new Date()
                    : undefined,
            // IsDeliveryNote: true,
            UsersActions: {
                CreatedDate: new Date()
            },
            Customer: {
                //SureName: 'Antonis',
                //BrandName: 'Zion Software',
                ContactDetails: [{} as ContactDetailType]
            } as CustomerType,
            CounterPart: {
                Address: {} as AddressType
            } as InvoicePartyType,
            Details: [],
            Summaries: [
                {
                    // TotalNetValue,
                    // TotalVATAmount,
                    // TotalGrossValue
                } as InvoiceSummaryType
            ],
            Incomes: [],
            Notes: [{} as InvoiceNoteType]
        } as InvoiceType;

        // Εδώ τη πρώτη φορα που θα ανοίξει παραστατικά, δεν θα έχει Παραστατικά. Η μεταβλητή invoiceDialogOptionsData.invoices θα είναι κενό array.
        switch (invoiceDialogOptionsData.DCLInvoiceTypeCode) {
            case '2.1': // 2.1: Τιμολόγιο Παροχής - Εδώ ο χρήστης ζήτησε Τιμολόγια
                this.UseWithDCLInvoiceTypeCode.set(true);

                const invoiceType_2_1 = this.ensureFormGroupValidArrays({
                    ...(invoiceDialogOptionsData.invoices?.find(
                        (invoice) => invoice.TypeCode == '2.1'
                    ) ?? { ...defaultInvoice, TypeCode: '2.1' })
                }); // Τιμολόγιο Παροχής
                const invoiceType_1_1 = this.ensureFormGroupValidArrays({
                    ...(invoiceDialogOptionsData.invoices?.find(
                        (invoice) => invoice.TypeCode == '1.1'
                    ) ?? { ...defaultInvoice, TypeCode: '1.1' })
                }); // Τιμολόγιο Πώλησης

                this.invoices.push(invoiceType_2_1);
                this.invoiceFormGroups.update((formGroup) => {
                    formGroup.push(
                        new InvoiceTypeFormGroup(
                            invoiceType_2_1,
                            invoiceFormGroupSelector
                        ).patchValueFromEntity(
                            invoiceType_2_1,
                            invoiceFormGroupSelector,
                            { emitEvent: false }
                        )
                    );
                    return formGroup;
                });

                this.invoices.push(invoiceType_1_1);
                this.invoiceFormGroups.update((formGroup) => {
                    formGroup.push(
                        new InvoiceTypeFormGroup(
                            invoiceType_1_1,
                            invoiceFormGroupSelector
                        ).patchValueFromEntity(
                            invoiceType_1_1,
                            invoiceFormGroupSelector,
                            { emitEvent: false }
                        )
                    );
                    return formGroup;
                });
                break;
            case '11.2': //  11.2: Α.Π.Υ - Εδώ ο χρήστης ζήτησε Αποδείξη Λιανικής
                this.UseWithDCLInvoiceTypeCode.set(true);

                const invoiceType_11_2 = this.ensureFormGroupValidArrays({
                    ...(invoiceDialogOptionsData.invoices?.find(
                        (invoice) => invoice.TypeCode == '11.2'
                    ) ?? { ...defaultInvoice, TypeCode: '11.2' })
                }); // Απόδειξη Παροχής
                const invoiceType_11_1 = this.ensureFormGroupValidArrays({
                    ...(invoiceDialogOptionsData.invoices?.find(
                        (invoice) => invoice.TypeCode == '11.1'
                    ) ?? { ...defaultInvoice, TypeCode: '11.1' })
                }); // Απόδειξη Πώλησης

                this.invoices.push(invoiceType_11_2);
                this.invoiceFormGroups.update((formGroup) => {
                    formGroup.push(
                        new InvoiceTypeFormGroup(
                            invoiceType_11_2,
                            invoiceFormGroupSelector
                        ).patchValueFromEntity(
                            invoiceType_11_2,
                            invoiceFormGroupSelector,
                            { emitEvent: false }
                        )
                    );
                    return formGroup;
                });

                this.invoices.push(invoiceType_11_1);
                this.invoiceFormGroups.update((formGroup) => {
                    formGroup.push(
                        new InvoiceTypeFormGroup(
                            invoiceType_11_1,
                            invoiceFormGroupSelector
                        ).patchValueFromEntity(
                            invoiceType_11_1,
                            invoiceFormGroupSelector
                        )
                    );
                    return formGroup;
                });

                break;
            default:
                // Εδώ δεν είμαστε από ψηφιακό πελατολόγιο, οπότε πρέπει να όρισουμε ότι χρειάζεται με βάση το input του χρήστη και όχι του ψηφιακού πελατολογίου
                const invoicesTypesCodes =
                    invoiceDialogOptionsData.invoicesTypesCodes ?? [];

                this.selectedInvoiceTab.set('none');

                invoiceDialogOptionsData.selectedInvoiceTab?.set(
                    this.selectedInvoiceTab()
                );

                invoicesTypesCodes
                    .filter(
                        (invoiceTypeCode) =>
                            (invoiceTypeCode?.trim() ?? '') != ''
                    )
                    .map((invoiceTypeCode) => {
                        const invoiceType = this.ensureFormGroupValidArrays({
                            ...(invoiceDialogOptionsData.invoices?.find(
                                (invoice) => invoice.TypeCode == invoiceTypeCode
                            ) ?? {
                                ...defaultInvoice,
                                TypeCode: invoiceTypeCode
                            })
                        });

                        this.invoices.push(invoiceType);
                        this.invoiceFormGroups.update((formGroup) => {
                            formGroup.push(
                                new InvoiceTypeFormGroup(
                                    invoiceType,
                                    invoiceFormGroupSelector
                                ).patchValueFromEntity(
                                    invoiceType,
                                    invoiceFormGroupSelector,
                                    { emitEvent: false }
                                )
                            );
                            return formGroup;
                        });
                    });

                break;
        }

        // this.invoice.Customer =
        //     this.invoice.Customer ??
        //     ({
        //         ContactDetails: [{} as ContactDetailType]
        //     } as CustomerType);

        // if ((this.invoice.Notes ?? []).length == 0)
        //     this.invoice.Notes = undefined;

        // this.invoice.Notes = this.invoice.Notes ?? [{} as InvoiceNoteType];

        // if ((this.invoice.Summaries ?? []).length == 0)
        //     this.invoice.Summaries = undefined;

        // this.invoice.Summaries = this.invoice.Summaries ?? [
        //     {} as InvoiceSummaryType
        // ];

        // if ((this.invoice.Incomes ?? []).length == 0)
        //     this.invoice.Incomes = undefined;

        // this.invoice.Incomes = this.invoice.Incomes ?? [];

        // debugger;
        // if (dialogOptions.mode == 'add') {
        //     // if user selected Add New Invoice initiate a new Invoice
        //     //dclCard: dialogOptions.dclCard,
        //     this.invoice = {
        //         TypeCode: dialogOptions.invoiceTypeCode, // '1.1',
        //         DispatchDate: new Date(),
        //         UsersActions: {
        //             CreatedDate: new Date()
        //         },
        //         Customer: {
        //             //SureName: 'Antonis',
        //             //BrandName: 'Zion Software',
        //             ContactDetails: [{} as ContactDetailType]
        //         } as CustomerType,
        //         CounterPart: {
        //             Address: {} as AddressType
        //         } as InvoicePartyType,
        //         Details: [],
        //         Summaries: [{} as InvoiceSummaryType],
        //         Incomes: [],
        //         Notes: [{} as InvoiceNoteType]
        //     } as InvoiceType;
        // }

        // this.invoice.DigitalClientCardId = dialogOptions.dclCard?._id;

        //this.invoice = this.invoices[0];

        debugger;

        //this.ensureFormGroupValidArrays();

        // this.invoiceFormGroup.set(
        //     this.invoiceFormGroups()[0] // new InvoiceTypeFormGroup(undefined, invoiceFormGroupSelector)
        // );

        // // Build and patch the InvoiceTypeFormGroup based on dialogOptions.invoiceType (edit invoice) or the this.invoice (new invoice) property
        // // NOTE: Do this patch to avoid erros like this: Cannot find control with path: 'Notes -> 0 -> Note'
        // //       We have to push records by default to the required arrays.
        // this.invoice = this.invoiceFormGroup()?.patchValueFromEntity(
        //     this.invoice,
        //     invoiceFormGroupSelector
        // ).value as InvoiceType;

        // // debugger;
        // // After building the invoiceFormGroup(), if we are in new mode, ensure at least one empty detail row
        // if (dialogOptions.mode == 'add')
        //     this.invoice = this.ensureLastEmptyRecord('forceNewRecord')
        //         ?.value as InvoiceType;

        // Register value changes
        this.invoiceFormGroups()[0]
            ?.valueChanges.pipe(
                takeUntilDestroyed(),
                distinctUntilChanged(),
                debounceTime(100)
            )
            .subscribe((value) => {
                this.calculateIncomesAndTaxes(
                    this.invoiceFormGroup(
                        this.selectedInvoiceTab() == 'none'
                            ? 'none'
                            : 'services'
                    )
                );
                this.ensureLastEmptyRecord(
                    this.selectedInvoiceTab() == 'none' ? 'none' : 'services'
                );

                // const invoice = this.invoiceFormGroup(
                //     'services'
                // ).getRawValue() as InvoiceType;

                // if (invoice) {
                //     invoiceDialogOptionsData.onFetchedInvoice?.(
                //         invoiceDialogOptionsData,
                //         invoice
                //     );
                // }

                // invoiceDialogOptionsData.invoiceCardTypeFormGroup =
                //     this.invoiceFormGroup('services');

                const invoice = this.invoiceFormGroup(
                    this.selectedInvoiceTab() == 'none' ? 'none' : 'services'
                ).getRawValue() as InvoiceType;

                if (invoice) {
                    invoiceDialogOptionsData.onFetchedInvoice?.(
                        invoiceDialogOptionsData
                    );
                }

                this.cd.detectChanges();
                this.invoiceCardCustomer?.detectChanges();
            });

        this.invoiceFormGroups()[1]
            ?.valueChanges.pipe(
                takeUntilDestroyed(),
                distinctUntilChanged(),
                debounceTime(100)
            )
            .subscribe((value) => {
                this.calculateIncomesAndTaxes(
                    this.invoiceFormGroup('products')
                );

                this.ensureLastEmptyRecord('products');

                // const invoice = this.invoiceFormGroup(
                //     'products'
                // ).getRawValue() as InvoiceType;

                // if (invoice) {
                //     invoiceDialogOptionsData.onFetchedInvoice?.(
                //         invoiceDialogOptionsData,
                //         invoice
                //     );
                // }

                // invoiceDialogOptionsData.invoiceCardTypeFormGroup =
                //     this.invoiceFormGroup('products');
                const invoice = this.invoiceFormGroup(
                    'products'
                ).getRawValue() as InvoiceType;

                if (invoice) {
                    invoiceDialogOptionsData.onFetchedInvoice?.(
                        invoiceDialogOptionsData
                    );
                }
                this.cd.detectChanges();
                this.invoiceCardCustomer?.detectChanges();
            });

        // this.invoiceFormGroup()
        //     ?.valueChanges.pipe(
        //         takeUntilDestroyed(),
        //         distinctUntilChanged(),
        //         debounceTime(100)
        //     )
        //     .subscribe((value) => {
        //         console.debug('invoiceFormGroupValue', {
        //             value: value,
        //             formGroup: this.invoiceFormGroup()
        //         });
        //         this.calculateIncomesAndTaxes(this.invoiceFormGroup());

        //         // if (value.UseRetailAfterTaxes != true)
        //         //     this.calculateIncomesAndTaxes(this.invoiceFormGroup());
        //         // else
        //         //     this.calculateIncomesAndTaxesWithRetailValues(
        //         //         this.invoiceFormGroup()
        //         //     );

        //         this.ensureLastEmptyRecord();
        //         this.cd.detectChanges();
        //         this.invoiceCardCustomer?.detectChanges();
        //         dialogOptions.invoiceCardTypeFormGroup =
        //             this.invoiceFormGroup();

        //         // debugger;
        //         // switch (value.TypeCode) {
        //         //     case '1.1':
        //         //         value.UseRetailAfterTaxes = true;
        //         // }
        //     });

        // Load Invoice, Product Packages, Branches, VATCategories e.t.c from database
        const opts = { emitEvent: false };

        // // get the finally invoice for edit (the new is also an edit)
        // const invoice: InvoiceType =
        //     dialogOptions.invoiceType ?? this.invoice ?? ({} as InvoiceType);

        invoiceDialogOptionsData.onDoPOSTransaction = (
            options: InvoiceOptions,
            lastSalePOSTransaction: POSTerminalTransactionType | undefined,
            POSResponse: POSTerminalResponseType | undefined
        ) => {
            this.fetchData().subscribe();
        };

        // Φέρε τα παραστατικά με βάση τι λέει το DCL Card.
        this.fetchData().subscribe();

        console.debug('InvoiceCardComponent - Initial FormValue: ', {
            //invoice: this.invoice,
            formGroupRawValue: this.invoiceFormGroup()?.getRawValue(),
            formGroup: this.invoiceFormGroup()
        });
    }

    selectedInvoiceTabChange(tabCode?: number | string) {
        debugger;
        this.selectedInvoiceTab.set(
            tabCode == 'services' ? 'services' : 'products'
        );

        const invoiceDialogOptionsData = this.dialogConfigDataOptions
            .data as InvoiceOptions;

        invoiceDialogOptionsData.selectedInvoiceTab?.set(
            this.selectedInvoiceTab()
        );

        const invoice = this.invoiceFormGroup().getRawValue() as InvoiceType;

        if (invoice) {
            invoiceDialogOptionsData.onFetchedInvoice?.(
                invoiceDialogOptionsData
            );
        }

        this.cd.detectChanges();
    }

    private fetchData(noLoader?: boolean) {
        debugger;
        const opts = { emitEvent: false };
        const invoiceDialogOptionsData = this.dialogConfigDataOptions
            .data as InvoiceOptions;

        // Get Invoices By Ids
        const params = QueryFilter.FetchQueryParamsForEntityId(
            this.invoices[0]?._id ?? '000000000000000000000000'
        );
        params.GroupQuery = QueryFilterGroup.JoinWithOrOperator([
            QueryFilterGroup.FieldIsEqualToObjectId(
                ['_id'],
                this.invoices[0]?._id ?? '000000000000000000000000'
            ),
            QueryFilterGroup.FieldIsEqualToObjectId(
                ['_id'],
                this.invoices[1]?._id ?? '000000000000000000000000'
            )
        ]);

        // Customer Params
        const customersParams = QueryFilter.FetchQueryParamsForEntityId(
            this.invoices[0]?.CustomerId ?? '000000000000000000000000'
        );
        customersParams.GroupQuery = QueryFilterGroup.JoinWithOrOperator([
            QueryFilterGroup.FieldIsEqualToObjectId(
                ['_id'],
                this.invoices[0]?.CustomerId ?? '000000000000000000000000'
            ),
            QueryFilterGroup.FieldIsEqualToObjectId(
                ['_id'],
                this.invoices[1]?.CustomerId ?? '000000000000000000000000'
            )
        ]);

        // Supplier Params
        const suppliersParams = QueryFilter.FetchQueryParamsForEntityId(
            this.invoices[0]?.SupplierId ?? '000000000000000000000000'
        );
        suppliersParams.GroupQuery = QueryFilterGroup.JoinWithOrOperator([
            QueryFilterGroup.FieldIsEqualToObjectId(
                ['_id'],
                this.invoices[0]?.SupplierId ?? '000000000000000000000000'
            ),
            QueryFilterGroup.FieldIsEqualToObjectId(
                ['_id'],
                this.invoices[1]?.SupplierId ?? '000000000000000000000000'
            )
        ]);

        // Packages Params
        const packagesParams = QueryFilter.FetchQueryParamsForEntityId(
            this.invoices[0]?._id ?? '000000000000000000000000'
        );
        const packageIds =
            this.invoices[0]?.Details?.filter(
                (detail) => (detail.PPackageId ?? '').length > 0
            )
                ?.map(
                    (detail) => detail.PPackageId ?? '000000000000000000000000'
                )
                .concat(
                    this.invoices[1]?.Details?.filter(
                        (detail) => (detail.PPackageId ?? '').length > 0
                    )?.map(
                        (detail) =>
                            detail.PPackageId ?? '000000000000000000000000'
                    ) ?? []
                ) ?? [];

        packagesParams.GroupQuery = QueryFilterGroup.JoinWithOrOperator([
            QueryFilterGroup.FieldIsEqualToObjectIds(['_id'], packageIds)
            // QueryFilterGroup.FieldIsEqualToObjectIds(
            //     ['_id'],
            //     this.invoices[1]?.Details?.filter(detail => (detail.PPackageId ?? '').length > 0)?.map(
            //         (detail) => detail.PPackageId ?? '000000000000000000000000'
            //     )
            // )
        ]);

        if (noLoader != true) this.loaderService.showAwaitLoader();
        return new Observable<void>((observer) => {
            this.store
                .dispatch([
                    new InvoiceGraphQLSchemaActions.Fetch(
                        queryForEdit,
                        params
                        // QueryFilter.FetchQueryParamsForEntityId(
                        //     this.invoices[0]?._id ?? '000000000000000000000000'
                        // )
                    ),
                    new ProductUnitTypeGraphQLSchemaActions.Fetch(
                        ProductUnitTypeTypeQuery.GQLQuery(),
                        undefined
                    ),
                    new VATCategoryGraphQLSchemaActions.Fetch(
                        VATCategoryTypeQuery.GQLQuery(),
                        undefined
                    ),
                    new BranchGraphQLSchemaActions.Fetch(
                        BranchTypeQuery.GQLQuery(),
                        undefined
                    ),
                    new CustomerGraphQLSchemaActions.Fetch(
                        {
                            ...CustomerTypeQuery.GQLQuery(),
                            Addresses: AddressTypeQuery.GQLQuery({
                                City: CityTypeQuery.GQLQuery({
                                    ...CityTypeQuery.GQLQuery(),
                                    Title: true
                                })
                            }),
                            FirstName: FirstNameTypeQuery.GQLQuery(),
                            FatherName: FirstNameTypeQuery.GQLQuery(),
                            InfoNotes: InfoNoteTypeQuery.GQLQuery(),
                            OccupationActivity:
                                OccupationActivityTypeQuery.GQLQuery(),
                            ContactDetails: ContactDetailTypeQuery.GQLQuery(),
                            // Courier: CourierTypeQuery.GQLQuery(),
                            FinancialDetails:
                                FinancialDetailTypeQuery.GQLQuery(),
                            TraderProfitMargin:
                                TraderProfitMarginTypeQuery.GQLQuery()
                        },
                        customersParams
                    ),

                    packageIds.length > 0
                        ? new ProductPackageGraphQLSchemaActions.Fetch(
                              ProductPackageTypeQuery.GQLQuery({
                                  Discounts:
                                      ProductToDiscountTypeQuery.GQLQuery(),
                                  UnitType: ProductUnitTypeTypeQuery.GQLQuery(),
                                  InventoriesList:
                                      ProductInventoryInfoTypeQuery.GQLQuery(),
                                  InvoicingList:
                                      ProductInvoicingInfoTypeQuery.GQLQuery({
                                          VATCategory:
                                              VATCategoryTypeQuery.GQLQuery()
                                      })
                              }),
                              packagesParams
                              //   {
                              //       GroupQuery: QueryFilterGroup.JoinWithOrOperator([
                              //           QueryFilterGroup.FieldIsEqualToObjectIds<ProductPackageType>(
                              //               ['_id'],
                              //               this.invoice?.Details?.map(
                              //                   (detail) =>
                              //                       detail.PPackageId ??
                              //                       '000000000000000000000000'
                              //               )?.filter(
                              //                   (ppackageId) => ppackageId != ''
                              //               )
                              //           )
                              //       ])
                              //   }
                          )
                        : new NoAction(),

                    // Supplier
                    new SupplierGraphQLSchemaActions.Fetch(
                        {
                            ...SupplierTypeQuery.GQLQuery(),
                            Addresses: AddressTypeQuery.GQLQuery({
                                City: CityTypeQuery.GQLQuery({})
                            }),
                            FirstName: FirstNameTypeQuery.GQLQuery(),
                            FatherName: FirstNameTypeQuery.GQLQuery(),
                            //InfoNotes: InfoNoteTypeQuery.GQLQuery(),
                            OccupationActivity:
                                OccupationActivityTypeQuery.GQLQuery(),
                            ContactDetails: ContactDetailTypeQuery.GQLQuery()
                            // Courier: CourierTypeQuery.GQLQuery(),
                            //FinancialDetails: FinancialDetailTypeQuery.GQLQuery(),
                        },
                        suppliersParams
                    ),
                    new POSTerminalGraphQLSchemaActions.Fetch(
                        POSTerminalTypeQuery.GQLQuery(),
                        undefined
                    )
                    // this.posTerminalSchema.Fetch(POSTerminalTypeQuery.GQLQuery(), undefined).subscribe(response => {
                    //   if (response.data.Fetch?.length == 0) {
                    //     this.posTerminalSchema.Upsert({
                    //       BranchId :0,

                    //     } as POSTerminal, undefined, POSTerminalTypeQuery.GQLQuery()).subscribe((response) => {
                    //       debugger;
                    //     })
                    //   }
                    // })
                ])
                .pipe(
                    finalize(() => {
                        if (noLoader != true)
                            this.loaderService.dismissAwaitLoader();

                        observer.complete();
                    })
                )
                .subscribe(() => {
                    // this.invoice = {
                    //     ...(this.store.selectSnapshot(
                    //         InvoiceGraphQLSchemaSelectors.FetchSelector('', '')
                    //     )?.data?.Fetch?.[0] ??
                    //         this.invoice ??
                    //         ({} as InvoiceType))
                    // };

                    const fetchedInvoices =
                        this.store.selectSnapshot(
                            InvoiceGraphQLSchemaSelectors.FetchSelector('', '')
                        )?.data?.Fetch ?? [];

                    const products = this.store.selectSnapshot(
                        ProductPackageGraphQLSchemaSelectors.FetchSelector(
                            '',
                            ''
                        )
                    );

                    const vatCategories = this.store.selectSnapshot(
                        VATCategoryGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data?.Fetch;

                    const customers = this.store.selectSnapshot(
                        CustomerGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data?.Fetch;

                    const branches = this.store.selectSnapshot(
                        BranchGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data?.Fetch;

                    const suppliers = this.store.selectSnapshot(
                        SupplierGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data?.Fetch;

                    const posTerminals = this.store.selectSnapshot(
                        POSTerminalGraphQLSchemaSelectors.FetchSelector('', '')
                    )?.data?.Fetch;
                    if (posTerminals?.length == 0) {
                        alert('No terminals found, insert a new one');
                        // Insert tst terminal
                        this.store.dispatch(
                            new POSTerminalGraphQLSchemaActions.Upsert(
                                {
                                    BranchId: branches?.[0]?._id,
                                    TerminalId: '80531138',
                                    IP: 'virtualpos.services.novidea.gr',
                                    Port: '4000',
                                    NSPId: '3',
                                    Title: 'Virtual Test Terminal - Cardlink'
                                } as POSTerminal,
                                undefined,
                                POSTerminalTypeQuery.GQLQuery()
                            )
                        );
                    }

                    this.productsUnitsTypes.set(
                        this.store.selectSnapshot(
                            ProductUnitTypeGraphQLSchemaSelectors.FetchSelector(
                                '',
                                ''
                            )
                        )?.data?.Fetch
                    );

                    this.vatCategories.set(vatCategories);

                    if (
                        invoiceDialogOptionsData.DCLInvoiceTypeCode != undefined
                    ) {
                        // Το 0 πρέπει να είναι το παροχής υπηρεσιών ή απόδειξη υπηρεσιών
                        const invoiceType_2_1 = fetchedInvoices.find(
                            (invoice) =>
                                invoice.TypeCode == '2.1' ||
                                invoice.TypeCode == '11.2'
                        );
                        this.invoices[0] = {
                            ...(invoiceType_2_1 ?? this.invoices[0]),
                            // Δένουμε το Ψηφιακό Πελατολόγιο
                            DigitalClientCardId:
                                invoiceDialogOptionsData.dclCard?._id,
                            // Δένουμε τον πελάτη
                            CustomerId:
                                invoiceType_2_1?.CustomerId ??
                                invoiceDialogOptionsData.dclCard?.CustomerId
                        };

                        // Το 1 πρέπει να είναι το πώλησης προΪόντωνν ή απόδειξη πώλησης
                        const invoiceType_1_1 = fetchedInvoices.find(
                            (invoice) =>
                                invoice.TypeCode == '1.1' ||
                                invoice.TypeCode == '11.1'
                        );
                        this.invoices[1] = {
                            ...(invoiceType_1_1 ?? this.invoices[1]),
                            // Δένουμε το Ψηφιακό Πελατολόγιο
                            DigitalClientCardId:
                                invoiceDialogOptionsData.dclCard?._id,
                            // Δένουμε τον πελάτη
                            CustomerId:
                                invoiceType_2_1?.CustomerId ??
                                invoiceDialogOptionsData.dclCard?.CustomerId
                        };

                        // this.invoice.DigitalClientCardId =
                        //     this.invoice.DigitalClientCardId ??
                        //     dialogOptions.dclCard?._id;

                        //this.ensureFormGroupValidArrays();

                        // // Build and patch the InvoiceTypeFormGroup based on dialogOptions.invoiceType (edit invoice) or the this.invoice (new invoice) property
                        // this.invoice = this.invoiceFormGroup()?.patchValueFromEntity(
                        //     this.invoice,
                        //     invoiceFormGroupSelector,
                        //     { emitEvent: false }
                        // ).value as InvoiceType;

                        // // After building the invoiceFormGroup(), if we are in new mode, ensure at least one empty detail row
                        // if (dialogOptions.mode == 'add')
                        //     this.invoice = this.ensureLastEmptyRecord('forceNewRecord')
                        //         ?.value as InvoiceType;

                        //this.loaderService.dismissAwaitLoader();
                        //this.invoice = { ...invoice };

                        // // Να ορίσουμε τις διευθύνσεις
                        // const postalAddress =
                        //     this.invoiceFormGroup()?.controls?.DeliveryNodeHeader?.controls?.DeliveryAddress?.getRawValue();
                        // if ((postalAddress?.PostalCode ?? '').trim() == '')
                        //     this.invoiceFormGroup()?.controls?.DeliveryNodeHeader?.controls?.DeliveryAddress?.controls?.PostalCode?.setValue(
                        //         '00000',
                        //         opts
                        //     );

                        // const counterPartAddtress =
                        //     this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.getRawValue();

                        // if ((counterPartAddtress?.PostalCode ?? '').trim() == '')
                        //     this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.PostalCode?.setValue(
                        //         '00000',
                        //         opts
                        //     );

                        // if ((counterPartAddtress?.StreetNumber ?? '').trim() == '')
                        //   this.formGroup?.controls?.CounterPart?.controls?.Address?.controls?.StreetNumber?.setValue(
                        //     '0',
                        //     opts,
                        //   );
                        //ddd

                        // Να ορίσουμε τα υποκαταστήματα
                        this.invoices[0].BranchId =
                            this.invoices[0].BranchId ?? branches?.[0]?._id;

                        // Path the Invoice's values into the form group
                        this.invoices[0] =
                            this.invoiceFormGroups()[0].patchValueFromEntity(
                                this.invoices[0],
                                invoiceFormGroupSelector
                            ).value as InvoiceType;

                        let disableInputs: boolean = true;

                        if (
                            this.invoices[0]?.Response?.InvoiceMark ==
                                undefined ||
                            (this.invoices[0]?.Response?.InvoiceMark == 0 &&
                                this.invoices[0]?.Response?.StatusCode?.toLocaleLowerCase() !=
                                    'Success'?.toLocaleLowerCase())
                        ) {
                            disableInputs = false;
                        }

                        if (disableInputs) {
                            this.invoiceFormGroups()[0]?.controls.CustomerId?.disable();
                            this.invoiceFormGroups()[0]?.controls.IssueDate?.disable();
                            this.invoiceFormGroups()[0]?.controls.DispatchDate?.disable();
                            this.invoiceFormGroups()[0]?.controls.IsDeliveryNote?.disable();
                            this.invoiceFormGroups()[0]?.controls.MovePurpose?.disable();
                        }

                        // ΠΡΟΣΟΧΗ: Εδώ κάνουμε disable τον πελάτη του παραστατικού
                        if (this.dclCard()?.CustomerId != undefined)
                            this.invoiceFormGroups()[0]?.controls.CustomerId?.disable();

                        console.debug(
                            'patchvalue for invoice Services: ',
                            this.invoiceFormGroups()[0].getRawValue()
                        );
                        this.invoices[1].BranchId =
                            this.invoices[1].BranchId ?? branches?.[0]?._id;

                        this.invoices[1] =
                            this.invoiceFormGroups()[1]?.patchValueFromEntity(
                                this.invoices[1],
                                invoiceFormGroupSelector
                            ).value as InvoiceType;
                        // ΠΡΟΣΟΧΗ: Εδώ κάνουμε disable τον πελάτη του παραστατικού
                        if (this.dclCard()?.CustomerId != undefined)
                            this.invoiceFormGroups()[1]?.controls.CustomerId?.disable();

                        disableInputs = true;

                        if (
                            this.invoices[1]?.Response?.InvoiceMark ==
                                undefined ||
                            (this.invoices[1]?.Response?.InvoiceMark == 0 &&
                                this.invoices[1]?.Response?.StatusCode?.toLocaleLowerCase() !=
                                    'Success'?.toLocaleLowerCase())
                        ) {
                            disableInputs = false;
                        }

                        if (disableInputs) {
                            this.invoiceFormGroups()[0]?.controls.CustomerId?.disable();
                            this.invoiceFormGroups()[0]?.controls.IssueDate?.disable();
                            this.invoiceFormGroups()[0]?.controls.DispatchDate?.disable();
                            this.invoiceFormGroups()[0]?.controls.IsDeliveryNote?.disable();
                            this.invoiceFormGroups()[0]?.controls.MovePurpose?.disable();
                        }

                        // this.invoice.BranchId =
                        //     this.invoice.BranchId ?? branches?.[0]?._id;

                        // this.invoice = this.invoiceFormGroup()?.patchValueFromEntity(
                        //     this.invoice,
                        //     invoiceFormGroupSelector
                        // ).value as InvoiceType;

                        // Να ορίσουμε για κάθε τύπο παραστατικού τα προΪόντα / υπηρεσίες και τα της αποθήκης
                        ['services', 'products'].map((tabId) =>
                            this.invoiceFormGroup(
                                tabId
                            )?.controls?.Details?.controls?.forEach(
                                (detailRowFormGroup) => {
                                    const invoice = this.invoiceFormGroup(
                                        tabId
                                    ).getRawValue() as InvoiceType;

                                    let disableInputs: boolean = true;

                                    if (
                                        invoice?.Response?.InvoiceMark ==
                                            undefined ||
                                        (invoice?.Response?.InvoiceMark == 0 &&
                                            invoice?.Response?.StatusCode?.toLocaleLowerCase() !=
                                                'Success'?.toLocaleLowerCase())
                                    ) {
                                        disableInputs = false;
                                    }

                                    if (disableInputs) {
                                        detailRowFormGroup.controls.Descr?.disable();
                                        detailRowFormGroup.controls.MUnit?.disable();
                                        detailRowFormGroup.controls.MUnitDescr?.disable();
                                        detailRowFormGroup.controls.Kind?.disable();
                                        detailRowFormGroup.controls.VATCategoryId?.disable();
                                        detailRowFormGroup.controls.Code?.disable();
                                        detailRowFormGroup.controls.Quantity?.disable();
                                        detailRowFormGroup.controls.UnitTypeId?.disable();
                                        detailRowFormGroup.controls.UnitValueUser?.disable();
                                    }

                                    // Αν είμαστε σε παραστατικό πώλησης προϊόντων απενεργοποιούμε το είδος γιατί δεν δέχεται τίποτε άλλο από προϊόν.
                                    if (tabId == 'products')
                                        detailRowFormGroup.controls.Kind?.disable();

                                    const productPackage =
                                        products?.data?.Fetch?.find(
                                            (item) =>
                                                item._id ==
                                                detailRowFormGroup.controls
                                                    .PPackageId?.value
                                        ); // products?.data?.Fetch?.[0];

                                    if (
                                        productPackage != undefined //detailFormGroup.controls.PPackageId?.value == productPackage?._id
                                    ) {
                                        detailRowFormGroup.controls.ProductPackage?.patchValue(
                                            productPackage as any,
                                            opts
                                        );

                                        detailRowFormGroup.controls.ProductPackage?.controls?.InventoriesList?.clear(
                                            opts
                                        );
                                        productPackage?.InventoriesList?.forEach(
                                            (inventoryList) => {
                                                detailRowFormGroup.controls.ProductPackage?.controls?.InventoriesList?.push(
                                                    new ProductInventoryInfoTypeFormGroup(
                                                        inventoryList
                                                    ),
                                                    opts
                                                );
                                            }
                                        );

                                        detailRowFormGroup.controls.ProductPackage?.controls?.InvoicingList?.clear(
                                            opts
                                        );
                                        productPackage?.InvoicingList?.forEach(
                                            (invoicing) => {
                                                detailRowFormGroup.controls.ProductPackage?.controls?.InvoicingList?.push(
                                                    new ProductInvoicingInfoTypeFormGroup(
                                                        invoicing
                                                    ),
                                                    opts
                                                );
                                            }
                                        );
                                    }
                                }
                            )
                        );

                        // Θέλουμε νέα κενή εγγραφή στο παραστατικό παρά μόνο αν δεν έχει details!!! (Στη βάση καταχωρείται και η κενή εγγραφή!!!)
                        if (this.invoices[0]?.Details?.length == 0)
                            this.ensureLastEmptyRecord(
                                'services',
                                'forceNewRecord'
                            );

                        if (this.invoices[1]?.Details?.length == 0)
                            this.ensureLastEmptyRecord(
                                'products',
                                'forceNewRecord'
                            );

                        //goodSaleSummary.StatusCode = goodSaleSummary.StatusCode ?? 'NEW';

                        // Initiate _id's objects, e.g Set Customer based on CustomerId field.

                        //this.invoice = this.invoiceFormGroup()?.value as InvoiceType;

                        //const invoiceDialogOptionsData = this.dialogConfig.data as InvoiceOptions;
                    } else {
                        // Εδώ είμαστε σε αποθήκευση παραστατικού που δεν έχει έρθει από Ψηφιακό Πελατολόγιο
                        this.invoices[0] = {
                            ...(fetchedInvoices[0] ?? this.invoices[0])
                        };

                        // Να ορίσουμε τα υποκαταστήματα
                        this.invoices[0].BranchId =
                            this.invoices[0].BranchId ?? branches?.[0]?._id;

                        // Path the Invoice's values into the form group
                        this.invoices[0] =
                            this.invoiceFormGroups()[0].patchValueFromEntity(
                                this.invoices[0],
                                invoiceFormGroupSelector
                            ).value as InvoiceType;

                        console.debug(
                            'patchvalue for invoice: ',
                            this.invoiceFormGroups()[0].getRawValue()
                        );

                        debugger;
                        let disableInputs: boolean = true;

                        if (
                            this.invoices[0]?.Response?.InvoiceMark ==
                                undefined ||
                            (this.invoices[0]?.Response?.InvoiceMark == 0 &&
                                this.invoices[0]?.Response?.StatusCode?.toLocaleLowerCase() !=
                                    'Success'?.toLocaleLowerCase())
                        ) {
                            disableInputs = false;
                        }

                        if (disableInputs) {
                            this.invoiceFormGroups()[0]?.controls.CustomerId?.disable();
                            this.invoiceFormGroups()[0]?.controls.IssueDate?.disable();
                            this.invoiceFormGroups()[0]?.controls.DispatchDate?.disable();
                            this.invoiceFormGroups()[0]?.controls.IsDeliveryNote?.disable();
                            this.invoiceFormGroups()[0]?.controls.MovePurpose?.disable();
                        }

                        this.invoiceFormGroups()[0]?.controls?.Details?.controls?.forEach(
                            (detailRowFormGroup) => {
                                const invoice =
                                    this.invoiceFormGroups()[0].getRawValue() as InvoiceType;
                                // Αν έχει εκδοθεί παραστατικό απενεργοποιούμε
                                if (disableInputs) {
                                    detailRowFormGroup.controls.Descr?.disable();
                                    detailRowFormGroup.controls.MUnit?.disable();
                                    detailRowFormGroup.controls.MUnitDescr?.disable();
                                    detailRowFormGroup.controls.Kind?.disable();
                                    detailRowFormGroup.controls.VATCategoryId?.disable();
                                    detailRowFormGroup.controls.Code?.disable();
                                    detailRowFormGroup.controls.Quantity?.disable();
                                    detailRowFormGroup.controls.UnitTypeId?.disable();
                                    detailRowFormGroup.controls.UnitValueUser?.disable();
                                }

                                // // Αν είμαστε σε παραστατικό πώλησης προϊόντων απενεργοποιούμε το είδος γιατί δεν δέχεται τίποτε άλλο από προϊόν.
                                // if (tabId == 'products')
                                //     detailRowFormGroup.controls.Kind?.disable();

                                const productPackage =
                                    products?.data?.Fetch?.find(
                                        (item) =>
                                            item._id ==
                                            detailRowFormGroup.controls
                                                .PPackageId?.value
                                    ); // products?.data?.Fetch?.[0];

                                if (
                                    productPackage != undefined //detailFormGroup.controls.PPackageId?.value == productPackage?._id
                                ) {
                                    detailRowFormGroup.controls.ProductPackage?.patchValue(
                                        productPackage as any,
                                        opts
                                    );

                                    detailRowFormGroup.controls.ProductPackage?.controls?.InventoriesList?.clear(
                                        opts
                                    );
                                    productPackage?.InventoriesList?.forEach(
                                        (inventoryList) => {
                                            detailRowFormGroup.controls.ProductPackage?.controls?.InventoriesList?.push(
                                                new ProductInventoryInfoTypeFormGroup(
                                                    inventoryList
                                                ),
                                                opts
                                            );
                                        }
                                    );

                                    detailRowFormGroup.controls.ProductPackage?.controls?.InvoicingList?.clear(
                                        opts
                                    );
                                    productPackage?.InvoicingList?.forEach(
                                        (invoicing) => {
                                            detailRowFormGroup.controls.ProductPackage?.controls?.InvoicingList?.push(
                                                new ProductInvoicingInfoTypeFormGroup(
                                                    invoicing
                                                ),
                                                opts
                                            );
                                        }
                                    );
                                }
                            }
                        );

                        // Θέλουμε νέα κενή εγγραφή στο παραστατικό παρά μόνο αν δεν έχει details!!! (Στη βάση καταχωρείται και η κενή εγγραφή!!!)
                        if (this.invoices[0]?.Details?.length == 0)
                            this.ensureLastEmptyRecord(
                                'none',
                                'forceNewRecord'
                            );
                    }

                    debugger;
                    if (customers?.[0] != undefined) {
                        this.selectedCustomer.set(customers?.[0]);
                    }

                    invoiceDialogOptionsData.invoiceCardTypeFormGroup =
                        this.invoiceFormGroup();
                    invoiceDialogOptionsData.invoiceCardTypeFormGroups =
                        this.invoiceFormGroups();

                    invoiceDialogOptionsData.selectedInvoiceTab?.set(
                        this.selectedInvoiceTab()
                    );

                    invoiceDialogOptionsData.onFetchedInvoice?.(
                        invoiceDialogOptionsData
                    );

                    observer.next();
                    this.cd.detectChanges();
                    //this.renderInvoice = true;
                });
        });
    }
    onInputFocus(rowIndex: number) {}

    onInputBlur(rowIndex: number) {}

    onMouseEnter(rowIndex: number) {
        this.selectedRowIndex = rowIndex;
    }

    onMouseLeave(rowIndex: number) {
        this.selectedRowIndex = undefined;
    }

    onInvoiceUseRetailPriceChange() {
        this.invoiceFormGroup()?.updateValueAndValidity();
    }

    deleteInvoiceDetail(rowIndex: number) {
        this.confirmationService.confirm({
            //target: event.target as EventTarget,
            message: 'Θέλετε να Διαγράψετε την εγγραφή?',
            // header: 'Επιβεβαίωση',
            defaultFocus: 'reject',
            closable: false,
            closeOnEscape: true,
            icon: 'pi pi-question',
            rejectButtonProps: {
                label: 'Ακύρωση',
                severity: 'secondary',
                outlined: true
            },
            acceptButtonProps: {
                label: 'Διαγραφή',
                outlined: true
            },
            accept: () => {
                // this.messageService.add({
                //     severity: 'info',
                //     summary: 'Confirmed',
                //     detail: 'You have accepted'
                // });
                debugger;
                if (
                    this.invoiceFormGroup()?.controls.Details?.controls?.[
                        rowIndex
                    ]?.controls._id?.value == undefined
                ) {
                    this.invoiceFormGroup()?.controls.Details?.controls.splice(
                        rowIndex,
                        1
                    );
                } else {
                    const invoiceDetail =
                        this.invoiceFormGroup()?.controls.Details?.controls?.[
                            rowIndex
                        ]?.getRawValue() as InvoiceDetailType;

                    this.loaderService.showAwaitLoader();
                    this.store
                        .dispatch(
                            new InvoiceDetailGraphQLSchemaActions.Delete(
                                invoiceDetail,
                                InvoiceDetailTypeQuery.GQLQuery()
                            )
                        )
                        .pipe(
                            finalize(() =>
                                this.loaderService.dismissAwaitLoader()
                            )
                        )
                        .subscribe(() => {
                            const deletedInvoiceDetail =
                                this.store.selectSnapshot(
                                    InvoiceDetailGraphQLSchemaSelectors.DeleteSelector(
                                        '',
                                        ''
                                    )
                                );

                            // this.invoiceFormGroup()?.controls.Details?.controls?.[
                            //     rowIndex
                            // ]?.controls.Deleted?.setValue(true);

                            this.invoiceFormGroup()?.controls.Details?.controls.splice(
                                rowIndex,
                                1
                            );

                            this.cd.detectChanges();
                        });
                }
            },
            reject: () => {
                // this.messageService.add({
                //     severity: 'error',
                //     summary: 'Rejected',
                //     detail: 'You have rejected',
                //     life: 3000,
                // });
            }
        });
    }

    /**
     * Get The active Invoice FormGroup based on Selected Tab
     * @returns The active Infoce Form Group
     */
    invoiceFormGroup(tabId?: 'services' | 'products' | 'none' | string) {
        // Αν δεν είμαστε από Ψηφιακό Πελατολόγιο τότε επιστρέφεις πάντα το 1ο Παραστατικό

        const selectedTabId = tabId ?? this.selectedInvoiceTab() ?? 'services';
        switch (selectedTabId) {
            case 'services':
            case 'none':
                return this.invoiceFormGroups()[0];
        }

        return this.invoiceFormGroups()[1];
        // const index = selectedTabId == 'services' ? 0 : 1;
        // return this.invoiceFormGroups()[index];
    }

    onProductSelect(event: {
        productPackage?: ProductPackageType;
        rowIndex: number;
    }) {
        const opts = { emitEvent: false };
        //const invoice = this.invoiceFormGroup()?.value as InvoiceType;

        const detail =
            this.invoiceFormGroup()?.controls?.Details?.controls?.[
                event.rowIndex
            ]?.controls;

        if (event.productPackage == undefined) {
            detail?.ProductPackage?.reset(undefined, opts);

            detail?.PPackageId?.reset(undefined, opts);
            detail?.PPackageStamp?.reset(undefined, opts);
        }

        if (event.productPackage != undefined && event.rowIndex != undefined) {
            const VATCategoryId =
                event.productPackage.InvoicingList?.[0]?.VATCategoryId ??
                event.productPackage.ProductDepartment?.VATCategoryId;
            const VATPercentage =
                event.productPackage.InvoicingList?.[0]?.VATCategory?.Value ??
                event.productPackage.ProductDepartment?.VATCategory?.Value;

            if (detail != undefined) {
                const customer =
                    this.invoiceFormGroup()?.controls?.Customer?.getRawValue();
                var discount = event.productPackage?.Discounts?.find(
                    (discount) =>
                        discount.DiscountCategoryId ==
                        customer?.DiscountCategoryId
                );

                detail.Kind?.setValue(
                    event.productPackage?.ProductDepartment?.IsService
                        ? 'S'
                        : 'M'
                );

                if (detail.Quantity?.value == undefined)
                    detail.Quantity?.setValue(1);

                if (detail.UnitValue?.value == undefined)
                    detail.UnitValue?.setValue(0);

                if (detail.Disc?.value == undefined) detail.Disc?.setValue(0);

                detail.Descr?.patchValue(event.productPackage.Title, opts);
                detail.Code?.patchValue(event.productPackage?.Code, opts);
                detail.UnitType?.patchValue(
                    event.productPackage.UnitType as any,
                    opts
                );
                detail.UnitTypeId?.patchValue(
                    event.productPackage.UnitTypeId,
                    opts
                );
                detail.MUnitDescr?.patchValue(
                    event.productPackage.UnitType?.Title,
                    opts
                );

                detail.VATCategoryId?.patchValue(VATCategoryId, opts);
                detail.VATPercent?.patchValue(VATPercentage, opts);
                detail.ProductPackage?.patchValue(
                    event.productPackage as any,
                    opts
                );
                //detail.d .DiscMode?.patchValue('P', opts); //Το είδος της έκτπωσης. 'C': Μετρητά, 'P': Ποσοστό.
                detail.Disc?.patchValue(discount?.Perentage, opts);

                // Η Αρχική Τιμή που πουλάει ο έμπορος (την ορίζει στη δομή InvoicingList)
                // detail.Value?.patchValue(
                //     event.productPackage.InvoicingList?.[0]?.WholesalePrice ??
                //         0,
                //     opts
                // );
                detail.UnitValue?.setValue(
                    event.productPackage.InvoicingList?.[0]?.WholesalePrice ??
                        0,
                    opts
                );

                // UnitValue

                const vatValue =
                    event.productPackage?.ProductDepartment?.VATCategory
                        ?.Value ?? 0;

                // Αν η Αξία Μονάδας Λιανικής που καταχωρεί ο χρήστης (UnitValueUser) αφορά αξία μετά φόρων ή προ φόρων ή  του τιμολογίου.
                // 'I': Τιμολογίου, 'B': Προ Φορων, 'A': Μετα Φόρων.
                // Έχει σημασία για να ξέρουμε πως θα υπολογίσουμε τη τιμή του UnitValue βάση αυτού που θέλει ο χρήστης.
                // Από Default ορίζουμε 'Ότι έχει το Παραστατικό'
                detail.UnitValueRetailType?.setValue('I', opts);

                // Τιμή Πώλησης (προ φόρων)'
                const retailPrice =
                    event.productPackage?.InvoicingList?.[0]?.RetailPrice;
                detail.UnitValueRetail?.setValue(retailPrice, opts);

                // Τιμή Πώλησης (μετά φόρων)'
                const retailPriceAfterTaxes =
                    event.productPackage?.InvoicingList?.[0]
                        ?.RetailPriceAfterTaxes;

                detail.UnitValueRetailAfterTaxes?.setValue(
                    retailPriceAfterTaxes,
                    opts
                );
                detail.UnitValueUser?.setValue(undefined);

                //detail.UnitValue?.setValue(retailPrice ?? 0);

                // TODO: Αυτός ο κώδικας να πάει στην calculatetaxes method
                // // Αν ο χρήστης ζητάει αξίες μετά φόρων
                // // τότε πρέπει να υπολογίσουμε όλες τις αξίες εδώ.
                // // Η calculateIncomesAndTaxesWithRetailValues θα κληθεί για να υπολογίσει μόνο τα σύνολα χωρίς υπολογισμούς Φ.Π.Α και χωρίς υπολογισμούς εκπτώσεων
                // if (invoice.UseRetailAfterTaxes == true) {
                //     const quantity = detail.Quantity?.value ?? 0; // 7 items
                //     const discount = detail.Disc?.value ?? 0; // 10%
                //     const unitValue = retailPriceAfterTaxes ?? 0; // 40 Euro  // detail.UnitValue?.setValue(retailPriceAfterTaxes ?? 0);

                //     let totalGrossValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros
                //     const discAmount = this.toFixed(totalGrossValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros
                //     const value = this.toFixed(totalGrossValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros

                //     detail.DiscAmount?.setValue(discAmount, opts);
                //     detail.Value?.setValue(value, opts);

                //     const summary = invoice.Summaries?.[0];
                //     const ovDiscAmount = this.toFixed(
                //         value * (summary?.DiscountPercent ?? 0)
                //     );

                //     detail.DiscOVAmount?.setValue(ovDiscAmount, opts);

                //     // Εδώ έχουμε τη τελική αξία πώλησης (μετά φόρων με τις εκπτώσεις της)
                //     // 7 Τεμ * 40 Ευρώ = 280 Ευρώ
                //     totalGrossValue = this.toFixed(value - ovDiscAmount);

                //     // Τώρα να υπολογίσουμε το Φ.Π.Α για αυτή τη τιμή
                //     // Αφαιρούμε απο την τελική αξία πώλησης το ποσοστό του Φ.Π.Α
                //     // 280 Ευρώ / 1.24 = 225.81 (225.8064516129)
                //     const retailPriceWithoutTAX = parseFloat(
                //         (totalGrossValue / (vatValue + 1)).toFixed(2)
                //     );

                //     // Υπολογίζουμε την αξία του Φ.Π.Α αφαιρόντας από την τελική αξία πώλησης την αξία πώλησης χωρίς Φ.Π.Α που υπολογίσαμε πριν.
                //     // 280 Ευρώ - 225.81 Ευρώ = 54.19  <- Αυτό είναι η αξία του Φ.Π.Α
                //     const netVAT = parseFloat(
                //         (totalGrossValue - retailPriceWithoutTAX).toFixed(2)
                //     );

                //     detail.VATAmount?.setValue(netVAT, opts);

                //     // Υπολογίζουμε εκ νέου τη καθαρή αρχική αξία
                //     // 280.00 Ευρώ - 54.19 Ευρώ = 225.81 Ευρώ
                //     const totalNetValue = parseFloat(
                //         (totalGrossValue - netVAT).toFixed(2)
                //     ); // 23.99 - 4.64 = 19.35

                //     //const kk: InvoiceDetailType | undefined = null;

                //     detail.NetValue?.setValue(totalNetValue, opts);
                //     detail.NetValueWithoutTaxes?.setValue(totalNetValue, opts);

                //     // Εδώ ορίζουμε τη αξία μονάδας διαιρώντας το σύνολο με τη ποσότητα
                //     // Φυσικά θα υπάρχουνπεριπτώσεις που δεν θα βγαίνει ακριβώς αλλά δεν γίνεται διαφορετικα΄.
                //     detail.UnitValue?.setValue(
                //         this.toFixed(totalNetValue / quantity),
                //         opts
                //     );
                // }

                // Obsolete Code
                // // Αν ο χρήστης έχει ορίσει RetailPrice πάνω στο προϊόν/υπηρεσία (πίνακας ProductPackage)
                // // τότε απλά θέσε σαν τιμή μονάδος το RetailPrice
                // if (retailPrice != undefined && (retailPrice ?? 0) > 0) {
                //     detail.UnitValue?.setValue(retailPrice);
                // }

                // // Αν ο χρήστης έχει ορίσει retailPriceAfterTaxes πάνω στο προϊόν/υπηρεσία (πίνακας ProductPackage)
                // // τότε υπολόγισε την αρχική τιμή αφαιρόντας το ποσοστό του Φ.Π.Α και κάνοντας τις ανάλογες πράξεις
                // // θέσε τη τιμή μονάδος.
                // if (
                //     retailPriceAfterTaxes != undefined &&
                //     vatValue != undefined &&
                //     (retailPriceAfterTaxes ?? 0) > 0 &&
                //     (vatValue ?? 0) > 0
                // ) {
                //     const retailPriceWithoutTAX = parseFloat(
                //         (retailPriceAfterTaxes / (vatValue + 1)).toFixed(2)
                //     ); // 23.99 / 1.24 = 19.3467741935 Euro

                //     const netVAT = parseFloat(
                //         (retailPriceAfterTaxes - retailPriceWithoutTAX).toFixed(
                //             2
                //         )
                //     ); // 23.99 - 19.35 = 4.64 Euro <- Αυτό είναι η αξία του Φ.Π.Α

                //     const unitValue = parseFloat(
                //         (retailPriceAfterTaxes - netVAT).toFixed(2)
                //     ); // 23.99 - 4.64 = 19.35

                //     if (unitValue * (vatValue + 1) != retailPriceAfterTaxes) {
                //         console.warn('Invalid unitValue.', {
                //             retailPriceAfterTaxes,
                //             retailPriceWithoutTAX,
                //             netVAT,
                //             unitValue
                //         });
                //     }

                //     detail.UnitValue?.setValue(unitValue);
                // }
                // //UnitValue

                detail.PPackageId?.setValue(event.productPackage._id, opts);
                detail.PPackageStamp?.setValue(
                    event.productPackage.Stamp,
                    opts
                );

                this.cd.detectChanges();
            }
        }

        console.debug(
            'Product - onRowDataSelect',
            this.invoiceFormGroup()?.getRawValue()
        );
    }

    /**
     * Calculate Incomings, Taxes and Total Values over the Invoices and patch the results to InvoiceForm Reactive Form.
     * @obsolete
     * @param formGroup
     * @param goodSaleSummary
     * @param invoiceFormGroup
     * @param groupItems
     * @returns
     */
    public calculateIncomesAndTaxes_original(formGroup?: InvoiceTypeFormGroup) {
        if (formGroup == undefined) return;
        const invoice: InvoiceType = formGroup?.getRawValue() as InvoiceType;

        const opts = { emitEvent: false };
        //const taxes = invoice.Taxes;
        const summary = invoice.Summaries?.[0];
        const details = invoice.Details;

        if (summary != undefined) {
            summary.Quantities = 0;
            summary.TotalNetValueBeforeDiscount = 0;
            summary.DiscountAmount = 0;
            summary.TotalNetValue = 0;
            summary.TotalVATAmount = 0;
            summary.TotalGrossValue = 0;

            // Reset Taxes Dictionary
            const taxesMap: Map<string, InvoiceTaxType> = new Map<
                string,
                InvoiceTaxType
            >();

            const incomesMap: Map<string, InvoiceIncomeClassificationType> =
                new Map<string, InvoiceIncomeClassificationType>();

            // invoice.Incomes?.map((income) => {
            //   incomesMap.set(`${income.Category}-${income.Type}`, income);
            // });
            details
                ?.filter((detail) => (detail.Descr ?? '').trim() != '')
                //?.filter((detail) => detail.Deleted != true)
                ?.map((detail, index) => {
                    const quantity = detail.Quantity ?? 0; // 6 items
                    const unitValue = detail.UnitValue ?? 0; // 5 Euro
                    const discount = detail.Disc ?? 0; // 10%
                    const totalNetValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros
                    const discAmount = this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros
                    const value = this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
                    const ovDiscAmount = this.toFixed(
                        value * (summary.DiscountPercent ?? 0)
                    );
                    // parseFloat(
                    //   (value * (summary.DiscountPercent ?? 0)).toFixed(3),
                    // ); // 27.00 * 3% = 0.81 Euros
                    const netValue = this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros

                    if (detail.Deleted != true) {
                        summary.TotalNetValueBeforeDiscount = this.toFixed(
                            (summary.TotalNetValueBeforeDiscount ?? 0) + value
                        );

                        summary.TotalNetValue = this.toFixed(
                            (summary.TotalNetValue ?? 0) + netValue
                        );
                    }
                    //const vatValue = this.toFixed(netValue * (detail.VATPercent ?? 0));

                    // Εδώ αθροίζουμε το σύνολο της NetValueAfterDiscount.
                });

            // Να εφαρμόσουμε το σύνολο της έκπτωσης στη τελική αξία προ φόρων και ότι περισσεύει να το περάσουμε στη πρώτη εγγραφή σαν διαφορά είτε θετική είτε αρνητική.
            let diff: number = 0;
            const totalNetValueBeforeDiscount =
                summary?.TotalNetValueBeforeDiscount;
            const discountPercent = summary?.DiscountPercent;

            if (discountPercent != undefined && discountPercent > 0) {
                const totalNetValueAfterTotalDiscount = this.toFixed(
                    totalNetValueBeforeDiscount -
                        totalNetValueBeforeDiscount * discountPercent
                ); // 395.90 - (395.90 * 0.03) => 395.90 - 11.877 => 384.023 => 384.02

                diff = this.toFixed(
                    summary.TotalNetValue - totalNetValueAfterTotalDiscount
                ); // 384.05 !== 384.02
            }

            // reset to zero those two variables
            summary.TotalNetValueBeforeDiscount = 0;
            summary.TotalNetValue = 0;

            details
                ?.filter((detail) => (detail.Descr ?? '').trim() != '')
                //?.filter((detail) => detail.Deleted != true)
                ?.map((detail, index) => {
                    /**
                     * Η Ποσότητα που ζητείται προς πώληση
                     */
                    const quantity = detail.Quantity ?? 0; // 6 items

                    /**
                     * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους)
                     */
                    const unitValue = detail.UnitValue ?? 0; // 5 Euro
                    const discount = detail.Disc ?? 0; // 10%

                    /**
                     * Το σύνολο της Αρχικής Αξία Πώλησης (είναι η ποσότητα * Αρχική Αξία Πώλησης της Μονάδας). Είναι Αξια πριν το Φ.Π.Α.
                     */
                    const totalNetValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros

                    const discAmount = this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros

                    /**
                     * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
                     */
                    const value = this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
                    let ovDiscAmount = this.toFixed(
                        value * (summary.DiscountPercent ?? 0)
                    );
                    // parseFloat(
                    //   (value * (summary.DiscountPercent ?? 0)).toFixed(3),
                    // ); // 27.00 * 3% = 0.81 Euros

                    /**
                     * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α
                     */
                    let netValue = this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros

                    if (diff != 0 && index == 0) {
                        // Εδώ είμαστε γιατί η έπτωση επί του συνόλου ειναι διαφορετική με την έκτπωση που έχει προκύψει από τον επιμερισμό λόγω στρογγυλοποιήσης.
                        // Άρα πρέπει αυτή τη διαφορά να τη περάσουμε σε κάποιο detail.
                        //
                        // Η Σωστή τιμή είναι η 'totalNetValueAfterTotalDiscount'.
                        // Τη διαφορά πρέπει να τη περάσουμε σε κάποιο είδος, ας πούμε στο πρώτο.
                        if (netValue + -diff >= 0) {
                            netValue += -diff; // Αντιστρέφουμε το πρόσιμο επειδή έχουμε έκπτωση (-), οπότε η καθαρή αξία είναι λίγοτερη
                            ovDiscAmount += diff; // και το ποσό της έκπτωσης μεγαλύτερο.

                            netValue = this.toFixed(netValue);
                            ovDiscAmount = this.toFixed(ovDiscAmount);
                        }
                    }

                    // console.debug('ovDiscount ', value, ovDiscAmount, netValue);

                    /**
                     * Η Αξία του Φ.Π.Α (Υπολογίζεται επί του netValue)
                     */
                    const vatValue = this.toFixed(
                        netValue * (detail.VATPercent ?? 0)
                    );

                    const detailFormGroup =
                        this.invoiceFormGroup()?.controls?.Details?.controls?.[
                            index
                        ];

                    detailFormGroup?.controls?.Line?.setValue(index + 1, opts);
                    detailFormGroup?.controls?.Value?.setValue(value, opts);

                    detailFormGroup?.controls?.DiscAmount?.setValue(
                        discAmount,
                        opts
                    );
                    detailFormGroup?.controls?.DiscOVAmount?.setValue(
                        ovDiscAmount,
                        opts
                    );

                    // Στην υλοποίηση του Backend, ο κώδικας που περιγράφει τα details του παραστατικού κάνει χρήση (στέλνει στον Πάροχο) αυτού του αντικειμένου με τα εξής πεδία:
                    // 1. ProviderInvoiceDetails.Quantity       => Η ποσότητα του είδους
                    // 2. ProviderInvoiceDetails.NetValue       => Η Τελική αξία πώλησης χωρίς όμως το Φ.Π.Α (Στην Α.Α.Δ.Ε δεν δέχεται σαν τελική αξία την αξία με το Φ.Π.Α)
                    //                                             Στο πεδίο αυτό της Α.Α.Δ.Ε στέλνουμε:
                    //                           NetValue               => Τη τιμή αυτού του πεδίου στέλνει ο Server στην Α.Α.Δ.Ε
                    //                           NetValueWithoutTaxes   => Αν όμως πρόκειται για παραστατικά Λιανικής τότε τη τιμή αυτού του πεδίου στέλνει ο Server στην Α.Α.Δ.Ε (11.1, 11.2, 11.3, 11.4, 11.5)
                    // 3. ProviderInvoiceDetails.VatAmount      => Η ΑΞία του Φ.Π.Α επί το σύνολο της γραμμής (Αυτή η Αξία + η NetValue μας δίνει τη Τελική Αξία Πώλησης προς τον πελάτη)
                    // 4. ProviderInvoiceDetails.LineUnitPrice  => Η Αξία μονάδοες

                    detailFormGroup?.controls?.NetValue?.setValue(
                        netValue,
                        opts
                    );

                    // // NOTE: Εδώ η τιμή του πεδίου NetValueWithoutTaxes έχει νόημα γιατί όταν μιλάμε για Απόδειξη Λιανικής
                    // //       το πεδίο NetValue 'μπορεί' να έχει τη τελική τιμή με το Φ.Π.Α.
                    // //       Στην παρούσα υλοποίηση δεν ισχύει αυτό αλλά ο server θέλει αυτό το πεδίο ενημερωμένο για Απόδειξη Λινικής.
                    // detailFormGroup?.controls?.NetValueWithoutTaxes?.setValue(
                    //     netValue,
                    //     opts
                    // );

                    detailFormGroup?.controls?.VATAmount?.setValue(
                        vatValue,
                        opts
                    );

                    //  parseFloat(
                    //   (netValue * (detail.VATPercent ?? 0)).toFixed(3),
                    // ); // 26.19 * 24% = 6.8256 => 6.83 Euros
                    //const grossValue = parseFloat((netValue + vatValue).toFixed(2)); // 26.19 + 6.83 = 33.02

                    let tax = taxesMap.get(detail.VATPercent?.toString() ?? '');
                    tax =
                        tax ??
                        ({
                            VATPercent: detail.VATPercent,
                            Amount: 0,
                            Value: 0
                        } as InvoiceTaxType);

                    tax.Amount = this.toFixed((tax.Amount ?? 0) + vatValue); // parseFloat(((tax.Amount ?? 0) + vatValue).toFixed(2));
                    tax.Value = this.toFixed((tax.Value ?? 0) + netValue); // parseFloat(((tax.Value ?? 0) + netValue).toFixed(2));

                    if (detail.Deleted != true) {
                        if ((detail.Quantity ?? 0) != 0)
                            taxesMap.set(tax.VATPercent?.toString() ?? '', tax);

                        summary.Quantities = this.toFixed(
                            (summary.Quantities ?? 0) + quantity
                        );
                        summary.TotalNetValueBeforeDiscount = this.toFixed(
                            (summary.TotalNetValueBeforeDiscount ?? 0) + value
                        );

                        summary.DiscountAmount = this.toFixed(
                            (summary.DiscountAmount ?? 0) + ovDiscAmount
                        );
                        summary.TotalNetValue = this.toFixed(
                            (summary.TotalNetValue ?? 0) + netValue
                        );

                        summary.TotalVATAmount = this.toFixed(
                            (summary.TotalVATAmount ?? 0) + vatValue
                        );

                        summary.TotalGrossValue = this.toFixed(
                            summary.TotalVATAmount + summary.TotalNetValue
                        );

                        if ((detail.Quantity ?? 0) != 0) {
                            // Incomes
                            // https://www.aade.gr/sites/default/files/2024-12/myDATA%20API%20Documentation%20v1%20.0.10_official_erp.pdf

                            // We have to group by Category and Type incomes. Then sum NetValue per group.
                            detail.IncomeCategory =
                                //detail.IncomeCategory ??
                                this.getIncomeClassificationCategory(
                                    invoice,
                                    detail
                                ) ?? 'category1_1'; // category1_1 Έσοδα από Πώληση Εμπορευμάτων (+) / (-)
                            detail.IncomeType =
                                //detail.IncomeType ??
                                this.getIncomeClassificationType(
                                    invoice,
                                    detail
                                ) ?? 'E3_561_001'; // E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών

                            detailFormGroup?.controls?.IncomeCategory?.setValue(
                                detail.IncomeCategory,
                                opts
                            );
                            detailFormGroup?.controls?.IncomeType?.setValue(
                                detail.IncomeType,
                                opts
                            );

                            const income =
                                incomesMap.get(
                                    `${detail.IncomeCategory}-${detail.IncomeType}`
                                ) ??
                                ({
                                    InvoiceId: detail?.InvoiceId,
                                    Amount: 0,
                                    Category: detail.IncomeCategory,
                                    Type: detail.IncomeType
                                } as InvoiceIncomeClassificationType);

                            income.Amount =
                                (income.Amount ?? 0) + (netValue ?? 0);

                            incomesMap.set(
                                `${detail.IncomeCategory}-${detail.IncomeType}`,
                                income
                            );
                        }
                    }
                });

            // We need to group details by income category and income type.
            // Set the new incomes records. Note that the backend will delete from the database any existing records before insert the new records.
            this.invoiceFormGroup()?.controls?.Incomes?.clear(opts);
            incomesMap?.forEach((income) => {
                this.invoiceFormGroup()?.controls?.Incomes?.controls?.push(
                    new InvoiceIncomeClassificationTypeFormGroup(income)
                );

                // Εδώ έχουμε ένα παράδοξο... το getRawValue() επιστρέφει τιμή αλλά το this.formGroup?.controls?.Incomes?.value δεν έχει τιμή, οπότε πρέπει να του ορίσουμε τιμή.
                const incomes =
                    (this.invoiceFormGroup()?.controls?.Incomes?.getRawValue() ??
                        []) as InvoiceIncomeClassificationType[];
                this.invoiceFormGroup()?.controls?.Incomes?.patchValue(
                    incomes as any,
                    opts
                );
            });

            this.taxesMap.set(taxesMap);

            const taxesFormControls =
                this.invoiceFormGroup()?.controls?.Taxes?.controls;

            taxesFormControls?.map((formControl) => {
                const vatPercent =
                    formControl.controls.VATPercent?.getRawValue();

                const tax = this.taxesMap().get(vatPercent?.toString() ?? '');
                formControl.controls.Amount?.setValue(tax?.Amount, opts);
                formControl.controls.Value?.setValue(tax?.Value, opts);
            });

            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.Quantities?.setValue(
                summary.Quantities,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalNetValueBeforeDiscount?.setValue(
                summary.TotalNetValueBeforeDiscount,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.DiscountAmount?.setValue(
                summary.DiscountAmount,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalNetValue?.setValue(
                summary.TotalNetValue,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalVATAmount?.setValue(
                summary.TotalVATAmount,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalGrossValue?.setValue(
                summary.TotalGrossValue,
                opts
            );

            const incomeFormGroup =
                this.invoiceFormGroup()?.controls.Incomes?.controls[0];
            if (incomeFormGroup != undefined) {
                incomeFormGroup.controls.Amount?.setValue(
                    summary.TotalNetValue,
                    opts
                );
            }
        }
    }

    /**
     * Calculate Incomings, Taxes and Total Values over the Invoices and patch the results to InvoiceForm Reactive Form.
     * @param formGroup
     * @param goodSaleSummary
     * @param invoiceFormGroup
     * @param groupItems
     * @returns
     */
    public calculateIncomesAndTaxes(formGroup?: InvoiceTypeFormGroup) {
        if (formGroup == undefined) return;
        const invoice: InvoiceType = formGroup?.getRawValue() as InvoiceType;

        const opts = { emitEvent: false };
        //const taxes = invoice.Taxes;
        const summary = invoice.Summaries?.[0];
        const details = invoice.Details;

        if (summary != undefined) {
            summary.Quantities = 0;
            summary.TotalNetValueBeforeDiscount = 0;
            summary.DiscountAmount = 0;
            summary.TotalNetValue = 0;
            summary.TotalVATAmount = 0;
            summary.TotalGrossValue = 0;

            // Reset Taxes Dictionary
            const taxesMap: Map<string, InvoiceTaxType> = new Map<
                string,
                InvoiceTaxType
            >();

            const incomesMap: Map<string, InvoiceIncomeClassificationType> =
                new Map<string, InvoiceIncomeClassificationType>();

            //  detail.UnitValueRetailType?.setValue('I');

            //             // Τιμή Πώλησης (προ φόρων)'
            //             const retailPrice =
            //                 event.productPackage?.InvoicingList?.[0]?.RetailPrice;
            //             detail.UnitValueRetail?.setValue(retailPrice);

            //             // Τιμή Πώλησης (μετά φόρων)'
            //             const retailPriceAfterTaxes =
            //                 event.productPackage?.InvoicingList?.[0]
            //                     ?.RetailPriceAfterTaxes;

            //             detail.UnitValueRetailAfterTaxes?.setValue(
            //                 retailPriceAfterTaxes
            //             );
            //             detail.UnitValueUser?.setValue(undefined);

            // invoice.Incomes?.map((income) => {
            //   incomesMap.set(`${income.Category}-${income.Type}`, income);
            // });

            details
                ?.filter((detail) => (detail.Descr ?? '').trim() != '')
                //?.filter((detail) => detail.Deleted != true)
                ?.map((detail, index) => {
                    const useRetailAfterTaxesEntryFlowForDetailRecord =
                        this.useRetailAfterTaxesEntryFlowForDetailRecord(
                            invoice,
                            detail
                        );

                    const quantity = detail.Quantity ?? 0; // 6 items
                    const unitValue = detail.UnitValue ?? 0; // 5 Euro
                    const discount = detail.Disc ?? 0; // 10%
                    const totalNetValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros
                    const discAmount = this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros
                    const value = this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
                    const ovDiscAmount = this.toFixed(
                        value * (summary.DiscountPercent ?? 0)
                    );

                    // parseFloat(
                    //   (value * (summary.DiscountPercent ?? 0)).toFixed(3),
                    // ); // 27.00 * 3% = 0.81 Euros
                    const netValue = this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros

                    const invoiceDetailValues = this.getDetailValues(
                        invoice,
                        detail,
                        summary,
                        useRetailAfterTaxesEntryFlowForDetailRecord
                    );
                    // TODO: +++++

                    if (detail.Deleted != true) {
                        summary.TotalNetValueBeforeDiscount = this.toFixed(
                            (summary.TotalNetValueBeforeDiscount ?? 0) +
                                invoiceDetailValues.value // value
                        );

                        summary.TotalNetValue = this.toFixed(
                            (summary.TotalNetValue ?? 0) +
                                invoiceDetailValues.netValue // netValue
                        );
                    }
                    //const vatValue = this.toFixed(netValue * (detail.VATPercent ?? 0));

                    // Εδώ αθροίζουμε το σύνολο της NetValueAfterDiscount.
                });

            // Να εφαρμόσουμε το σύνολο της έκπτωσης στη τελική αξία προ φόρων και ότι περισσεύει να το περάσουμε στη πρώτη εγγραφή σαν διαφορά είτε θετική είτε αρνητική.

            let diff: number = 0;
            const totalNetValueBeforeDiscount =
                summary?.TotalNetValueBeforeDiscount;
            const discountPercent = summary?.DiscountPercent;

            if (discountPercent != undefined && discountPercent > 0) {
                const totalNetValueAfterTotalDiscount = this.toFixed(
                    totalNetValueBeforeDiscount -
                        totalNetValueBeforeDiscount * discountPercent
                ); // 395.90 - (395.90 * 0.03) => 395.90 - 11.877 => 384.023 => 384.02

                diff = this.toFixed(
                    summary.TotalNetValue - totalNetValueAfterTotalDiscount
                ); // 384.05 !== 384.02
            }

            // reset to zero those two variables
            summary.TotalNetValueBeforeDiscount = 0;
            summary.TotalNetValue = 0;

            details
                ?.filter((detail) => (detail.Descr ?? '').trim() != '')
                //?.filter((detail) => detail.Deleted != true)
                ?.map((detail, index) => {
                    const useRetailAfterTaxesEntryFlowForDetailRecord =
                        this.useRetailAfterTaxesEntryFlowForDetailRecord(
                            invoice,
                            detail
                        );

                    const invoiceDetailValues = this.getDetailValues(
                        invoice,
                        detail,
                        summary,
                        useRetailAfterTaxesEntryFlowForDetailRecord
                    );

                    /**
                     * Η Ποσότητα που ζητείται προς πώληση
                     */
                    const quantity = invoiceDetailValues.quantity; // detail.Quantity ?? 0; // 6 items

                    /**
                     * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους)
                     */
                    const unitValue = invoiceDetailValues.unitValue; // detail.UnitValue ?? 0; // 5 Euro
                    const discount = invoiceDetailValues.discount ?? 0; // detail.Disc ?? 0; // 10%

                    /**
                     * Το σύνολο της Αρχικής Αξία Πώλησης (είναι η ποσότητα * Αρχική Αξία Πώλησης της Μονάδας). Είναι Αξια πριν το Φ.Π.Α.
                     */
                    const totalNetValue = invoiceDetailValues.netValue; // this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros

                    const discAmount = invoiceDetailValues.discAmount; // this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros

                    /**
                     * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
                     */
                    const value = invoiceDetailValues.value; // this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
                    let ovDiscAmount = invoiceDetailValues.ovDiscAmount ?? 0;
                    //  this.toFixed(
                    //     value * (summary.DiscountPercent ?? 0)
                    // );
                    // parseFloat(
                    //   (value * (summary.DiscountPercent ?? 0)).toFixed(3),
                    // ); // 27.00 * 3% = 0.81 Euros

                    /**
                     * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α
                     */
                    let netValue = invoiceDetailValues.netValue; // this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros

                    if (diff != 0 && index == 0) {
                        // Εδώ είμαστε γιατί η έπτωση επί του συνόλου ειναι διαφορετική με την έκτπωση που έχει προκύψει από τον επιμερισμό λόγω στρογγυλοποιήσης.
                        // Άρα πρέπει αυτή τη διαφορά να τη περάσουμε σε κάποιο detail.
                        //
                        // Η Σωστή τιμή είναι η 'totalNetValueAfterTotalDiscount'.
                        // Τη διαφορά πρέπει να τη περάσουμε σε κάποιο είδος, ας πούμε στο πρώτο.
                        if (netValue + -diff >= 0) {
                            netValue += -diff; // Αντιστρέφουμε το πρόσιμο επειδή έχουμε έκπτωση (-), οπότε η καθαρή αξία είναι λίγοτερη
                            ovDiscAmount += diff; // και το ποσό της έκπτωσης μεγαλύτερο.

                            netValue = this.toFixed(netValue);
                            ovDiscAmount = this.toFixed(ovDiscAmount);
                        }
                    }

                    // console.debug('ovDiscount ', value, ovDiscAmount, netValue);

                    /**
                     * Η Αξία του Φ.Π.Α (Υπολογίζεται επί του netValue)
                     */
                    const vatValue = invoiceDetailValues.vatValue;
                    //  this.toFixed(
                    //     netValue * (detail.VATPercent ?? 0)
                    // );

                    const detailFormGroup =
                        formGroup?.controls?.Details?.controls?.[index];

                    detailFormGroup?.controls?.Line?.setValue(index + 1, opts);

                    // TODO: +++++
                    detailFormGroup?.controls?.UnitValue?.setValue(
                        invoiceDetailValues.unitValue,
                        opts
                    );
                    detailFormGroup?.controls?.UnitValueUser?.setValue(
                        invoiceDetailValues.UnitValueUser,
                        opts
                    );

                    detailFormGroup?.controls?.Value?.setValue(value, opts);

                    detailFormGroup?.controls?.DiscAmount?.setValue(
                        discAmount,
                        opts
                    );
                    detailFormGroup?.controls?.DiscOVAmount?.setValue(
                        ovDiscAmount,
                        opts
                    );

                    // Στην υλοποίηση του Backend, ο κώδικας που περιγράφει τα details του παραστατικού κάνει χρήση (στέλνει στον Πάροχο) αυτού του αντικειμένου με τα εξής πεδία:
                    // 1. ProviderInvoiceDetails.Quantity       => Η ποσότητα του είδους
                    // 2. ProviderInvoiceDetails.NetValue       => Η Τελική αξία πώλησης χωρίς όμως το Φ.Π.Α (Στην Α.Α.Δ.Ε δεν δέχεται σαν τελική αξία την αξία με το Φ.Π.Α)
                    //                                             Στο πεδίο αυτό της Α.Α.Δ.Ε στέλνουμε:
                    //                           NetValue               => Τη τιμή αυτού του πεδίου στέλνει ο Server στην Α.Α.Δ.Ε
                    //                           NetValueWithoutTaxes   => Αν όμως πρόκειται για παραστατικά Λιανικής τότε τη τιμή αυτού του πεδίου στέλνει ο Server στην Α.Α.Δ.Ε (11.1, 11.2, 11.3, 11.4, 11.5)
                    // 3. ProviderInvoiceDetails.VatAmount      => Η ΑΞία του Φ.Π.Α επί το σύνολο της γραμμής (Αυτή η Αξία + η NetValue μας δίνει τη Τελική Αξία Πώλησης προς τον πελάτη)
                    // 4. ProviderInvoiceDetails.LineUnitPrice  => Η Αξία μονάδοες

                    detailFormGroup?.controls?.NetValue?.setValue(
                        netValue,
                        opts
                    );

                    detailFormGroup?.controls?.NetValueUser?.setValue(
                        netValue,
                        opts
                    );

                    // // NOTE: Εδώ η τιμή του πεδίου NetValueWithoutTaxes έχει νόημα γιατί όταν μιλάμε για Απόδειξη Λιανικής
                    // //       το πεδίο NetValue 'μπορεί' να έχει τη τελική τιμή με το Φ.Π.Α.
                    // //       Στην παρούσα υλοποίηση δεν ισχύει αυτό αλλά ο server θέλει αυτό το πεδίο ενημερωμένο για Απόδειξη Λινικής.
                    // detailFormGroup?.controls?.NetValueWithoutTaxes?.setValue(
                    //     netValue,
                    //     opts
                    // );

                    detailFormGroup?.controls?.VATAmount?.setValue(
                        vatValue,
                        opts
                    );

                    // TODO: +++++

                    //  parseFloat(
                    //   (netValue * (detail.VATPercent ?? 0)).toFixed(3),
                    // ); // 26.19 * 24% = 6.8256 => 6.83 Euros
                    //const grossValue = parseFloat((netValue + vatValue).toFixed(2)); // 26.19 + 6.83 = 33.02

                    let tax = taxesMap.get(detail.VATPercent?.toString() ?? '');
                    tax =
                        tax ??
                        ({
                            VATPercent: detail.VATPercent,
                            Amount: 0,
                            Value: 0
                        } as InvoiceTaxType);

                    tax.Amount = this.toFixed((tax.Amount ?? 0) + vatValue); // parseFloat(((tax.Amount ?? 0) + vatValue).toFixed(2));
                    tax.Value = this.toFixed((tax.Value ?? 0) + netValue); // parseFloat(((tax.Value ?? 0) + netValue).toFixed(2));

                    if (detail.Deleted != true) {
                        if ((detail.Quantity ?? 0) != 0)
                            taxesMap.set(tax.VATPercent?.toString() ?? '', tax);

                        summary.Quantities = this.toFixed(
                            (summary.Quantities ?? 0) + quantity
                        );
                        summary.TotalNetValueBeforeDiscount = this.toFixed(
                            (summary.TotalNetValueBeforeDiscount ?? 0) + value
                        );

                        summary.DiscountAmount = this.toFixed(
                            (summary.DiscountAmount ?? 0) + ovDiscAmount
                        );
                        summary.TotalNetValue = this.toFixed(
                            (summary.TotalNetValue ?? 0) + netValue
                        );

                        summary.TotalVATAmount = this.toFixed(
                            (summary.TotalVATAmount ?? 0) + vatValue
                        );

                        summary.TotalGrossValue = this.toFixed(
                            summary.TotalVATAmount + summary.TotalNetValue
                        );

                        if ((detail.Quantity ?? 0) != 0) {
                            // Incomes
                            // https://www.aade.gr/sites/default/files/2024-12/myDATA%20API%20Documentation%20v1%20.0.10_official_erp.pdf

                            // We have to group by Category and Type incomes. Then sum NetValue per group.
                            detail.IncomeCategory =
                                //detail.IncomeCategory ??
                                this.getIncomeClassificationCategory(
                                    invoice,
                                    detail
                                ) ?? 'category1_1'; // category1_1 Έσοδα από Πώληση Εμπορευμάτων (+) / (-)
                            detail.IncomeType =
                                //detail.IncomeType ??
                                this.getIncomeClassificationType(
                                    invoice,
                                    detail
                                ) ?? 'E3_561_001'; // E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών

                            detailFormGroup?.controls?.IncomeCategory?.setValue(
                                detail.IncomeCategory,
                                opts
                            );
                            detailFormGroup?.controls?.IncomeType?.setValue(
                                detail.IncomeType,
                                opts
                            );

                            const income =
                                incomesMap.get(
                                    `${detail.IncomeCategory}-${detail.IncomeType}`
                                ) ??
                                ({
                                    InvoiceId: detail?.InvoiceId,
                                    Amount: 0,
                                    Category: detail.IncomeCategory,
                                    Type: detail.IncomeType
                                } as InvoiceIncomeClassificationType);

                            income.Amount =
                                (income.Amount ?? 0) + (netValue ?? 0);

                            incomesMap.set(
                                `${detail.IncomeCategory}-${detail.IncomeType}`,
                                income
                            );
                        }
                    }
                });

            // We need to group details by income category and income type.
            // Set the new incomes records. Note that the backend will delete from the database any existing records before insert the new records.
            formGroup?.controls?.Incomes?.clear(opts);
            incomesMap?.forEach((income) => {
                formGroup?.controls?.Incomes?.controls?.push(
                    new InvoiceIncomeClassificationTypeFormGroup(income)
                );

                // Εδώ έχουμε ένα παράδοξο... το getRawValue() επιστρέφει τιμή αλλά το this.formGroup?.controls?.Incomes?.value δεν έχει τιμή, οπότε πρέπει να του ορίσουμε τιμή.
                const incomes = (formGroup?.controls?.Incomes?.getRawValue() ??
                    []) as InvoiceIncomeClassificationType[];
                formGroup?.controls?.Incomes?.patchValue(incomes as any, opts);
            });

            this.taxesMap.set(taxesMap);

            const taxesFormControls = formGroup?.controls?.Taxes?.controls;

            taxesFormControls?.map((formControl) => {
                const vatPercent =
                    formControl.controls.VATPercent?.getRawValue();

                const tax = this.taxesMap().get(vatPercent?.toString() ?? '');
                formControl.controls.Amount?.setValue(tax?.Amount, opts);
                formControl.controls.Value?.setValue(tax?.Value, opts);
            });

            formGroup?.controls?.Summaries?.controls[0].controls.Quantities?.setValue(
                summary.Quantities,
                opts
            );
            formGroup?.controls?.Summaries?.controls[0].controls.TotalNetValueBeforeDiscount?.setValue(
                summary.TotalNetValueBeforeDiscount,
                opts
            );
            formGroup?.controls?.Summaries?.controls[0].controls.DiscountAmount?.setValue(
                summary.DiscountAmount,
                opts
            );
            formGroup?.controls?.Summaries?.controls[0].controls.TotalNetValue?.setValue(
                summary.TotalNetValue,
                opts
            );
            formGroup?.controls?.Summaries?.controls[0].controls.TotalVATAmount?.setValue(
                summary.TotalVATAmount,
                opts
            );
            formGroup?.controls?.Summaries?.controls[0].controls.TotalGrossValue?.setValue(
                summary.TotalGrossValue,
                opts
            );

            const incomeFormGroup = formGroup?.controls.Incomes?.controls[0];
            if (incomeFormGroup != undefined) {
                incomeFormGroup.controls.Amount?.setValue(
                    summary.TotalNetValue,
                    opts
                );
            }
        }
    }

    /**
     * Calculate Incomings, Taxes and Total Values over the Invoices and patch the results to InvoiceForm Reactive Form.
     * Use Retail Values.
     * @Obsolete use
     * @param formGroup
     * @param goodSaleSummary
     * @param invoiceFormGroup
     * @param groupItems
     * @returns
     */
    public calculateIncomesAndTaxesWithRetailValues_OBSOLETE(
        formGroup?: InvoiceTypeFormGroup
    ) {
        if (formGroup == undefined) return;
        const invoice: InvoiceType = formGroup?.getRawValue() as InvoiceType;

        const opts = { emitEvent: false };
        //const taxes = invoice.Taxes;
        const summary = invoice.Summaries?.[0];
        const details = invoice.Details;

        if (summary != undefined) {
            summary.Quantities = 0;
            summary.TotalNetValueBeforeDiscount = 0;
            summary.DiscountAmount = 0;
            summary.TotalNetValue = 0;
            summary.TotalVATAmount = 0;
            summary.TotalGrossValue = 0;

            // Reset Taxes Dictionary
            const taxesMap: Map<string, InvoiceTaxType> = new Map<
                string,
                InvoiceTaxType
            >();

            const incomesMap: Map<string, InvoiceIncomeClassificationType> =
                new Map<string, InvoiceIncomeClassificationType>();

            // invoice.Incomes?.map((income) => {
            //   incomesMap.set(`${income.Category}-${income.Type}`, income);
            // });

            invoice.UseRetailAfterTaxes;
            details
                ?.filter((detail) => (detail.Descr ?? '').trim() != '')
                //?.filter((detail) => detail.Deleted != true)
                ?.map((detail, index) => {
                    // const quantity = detail.Quantity ?? 0; // 6 items
                    // const unitValue = detail.UnitValue ?? 0; // 5 Euro
                    // const discount = detail.Disc ?? 0; // 10%
                    // const totalNetValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros
                    // const discAmount = this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros
                    // const value = this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
                    // const ovDiscAmount = this.toFixed(
                    //     value * (summary.DiscountPercent ?? 0)
                    // );
                    // // parseFloat(
                    // //   (value * (summary.DiscountPercent ?? 0)).toFixed(3),
                    // // ); // 27.00 * 3% = 0.81 Euros
                    // const netValue = this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros
                    if (detail.Deleted != true) {
                        summary.TotalNetValueBeforeDiscount = this.toFixed(
                            (summary.TotalNetValueBeforeDiscount ?? 0) +
                                (detail?.NetValue ?? 0) // value
                        );

                        summary.TotalNetValue = this.toFixed(
                            (summary.TotalNetValue ?? 0) +
                                (detail?.NetValue ?? 0) // netValue
                        );
                    }
                    //const vatValue = this.toFixed(netValue * (detail.VATPercent ?? 0));

                    // Εδώ αθροίζουμε το σύνολο της NetValueAfterDiscount.
                });

            // Να εφαρμόσουμε το σύνολο της έκπτωσης στη τελική αξία προ φόρων και ότι περισσεύει να το περάσουμε στη πρώτη εγγραφή σαν διαφορά είτε θετική είτε αρνητική.
            let diff: number = 0;
            const totalNetValueBeforeDiscount =
                summary?.TotalNetValueBeforeDiscount;
            const discountPercent = summary?.DiscountPercent;

            if (discountPercent != undefined && discountPercent > 0) {
                const totalNetValueAfterTotalDiscount = this.toFixed(
                    totalNetValueBeforeDiscount -
                        totalNetValueBeforeDiscount * discountPercent
                ); // 395.90 - (395.90 * 0.03) => 395.90 - 11.877 => 384.023 => 384.02

                diff = this.toFixed(
                    summary.TotalNetValue - totalNetValueAfterTotalDiscount
                ); // 384.05 !== 384.02
            }

            // reset to zero those two variables
            summary.TotalNetValueBeforeDiscount = 0;
            summary.TotalNetValue = 0;

            details
                ?.filter((detail) => (detail.Descr ?? '').trim() != '')
                //?.filter((detail) => detail.Deleted != true)
                ?.map((detail, index) => {
                    /**
                     * Η Ποσότητα που ζητείται προς πώληση
                     */
                    const quantity = detail.Quantity ?? 0; // 6 items

                    /**
                     * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους)
                     */
                    const unitValue = detail.UnitValue ?? 0; // 5 Euro
                    const discount = detail.Disc ?? 0; // 10%

                    /**
                     * Το σύνολο της Αρχικής Αξία Πώλησης (είναι η ποσότητα * Αρχική Αξία Πώλησης της Μονάδας). Είναι Αξια πριν το Φ.Π.Α.
                     */
                    const totalNetValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros

                    const discAmount = this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros

                    /**
                     * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
                     */
                    const value = this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
                    let ovDiscAmount = this.toFixed(
                        value * (summary.DiscountPercent ?? 0)
                    );
                    // parseFloat(
                    //   (value * (summary.DiscountPercent ?? 0)).toFixed(3),
                    // ); // 27.00 * 3% = 0.81 Euros

                    /**
                     * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α
                     */
                    let netValue = this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros

                    if (diff != 0 && index == 0) {
                        // Εδώ είμαστε γιατί η έπτωση επί του συνόλου ειναι διαφορετική με την έκτπωση που έχει προκύψει από τον επιμερισμό λόγω στρογγυλοποιήσης.
                        // Άρα πρέπει αυτή τη διαφορά να τη περάσουμε σε κάποιο detail.
                        //
                        // Η Σωστή τιμή είναι η 'totalNetValueAfterTotalDiscount'.
                        // Τη διαφορά πρέπει να τη περάσουμε σε κάποιο είδος, ας πούμε στο πρώτο.
                        if (netValue + -diff >= 0) {
                            netValue += -diff; // Αντιστρέφουμε το πρόσιμο επειδή έχουμε έκπτωση (-), οπότε η καθαρή αξία είναι λίγοτερη
                            ovDiscAmount += diff; // και το ποσό της έκπτωσης μεγαλύτερο.

                            netValue = this.toFixed(netValue);
                            ovDiscAmount = this.toFixed(ovDiscAmount);
                        }
                    }

                    // console.debug('ovDiscount ', value, ovDiscAmount, netValue);

                    const vatValue = this.toFixed(
                        netValue * (detail.VATPercent ?? 0)
                    );

                    const detailFormGroup =
                        this.invoiceFormGroup()?.controls?.Details?.controls?.[
                            index
                        ];

                    detailFormGroup?.controls?.Line?.setValue(index + 1, opts);
                    // detailFormGroup?.controls?.Value?.setValue(value, opts);

                    // detailFormGroup?.controls?.DiscAmount?.setValue(
                    //     discAmount,
                    //     opts
                    // );
                    // detailFormGroup?.controls?.DiscOVAmount?.setValue(
                    //     ovDiscAmount,
                    //     opts
                    // );
                    // detailFormGroup?.controls?.NetValue?.setValue(
                    //     netValue,
                    //     opts
                    // );

                    // // NOTE: Εδώ η τιμή του πεδίου NetValueWithoutTaxes έχει νόημα γιατί όταν μιλάμε για Απόδειξη Λιανικής
                    // //       το πεδίο NetValue 'μπορεί' να έχει τη τελική τιμή με το Φ.Π.Α.
                    // //       Στην παρούσα υλοποίηση δεν ισχύει αυτό αλλά ο server θέλει αυτό το πεδίο ενημερωμένο για Απόδειξη Λινικής.
                    // detailFormGroup?.controls?.NetValueWithoutTaxes?.setValue(
                    //     netValue,
                    //     opts
                    // );

                    // detailFormGroup?.controls?.VATAmount?.setValue(
                    //     vatValue,
                    //     opts
                    // );

                    //  parseFloat(
                    //   (netValue * (detail.VATPercent ?? 0)).toFixed(3),
                    // ); // 26.19 * 24% = 6.8256 => 6.83 Euros
                    //const grossValue = parseFloat((netValue + vatValue).toFixed(2)); // 26.19 + 6.83 = 33.02

                    let tax = taxesMap.get(detail.VATPercent?.toString() ?? '');
                    tax =
                        tax ??
                        ({
                            VATPercent: detail.VATPercent,
                            Amount: 0,
                            Value: 0
                        } as InvoiceTaxType);

                    tax.Amount = this.toFixed((tax.Amount ?? 0) + vatValue); // parseFloat(((tax.Amount ?? 0) + vatValue).toFixed(2));
                    tax.Value = this.toFixed((tax.Value ?? 0) + netValue); // parseFloat(((tax.Value ?? 0) + netValue).toFixed(2));

                    if (detail.Deleted != true) {
                        if ((detail.Quantity ?? 0) != 0)
                            taxesMap.set(tax.VATPercent?.toString() ?? '', tax);

                        summary.Quantities = this.toFixed(
                            (summary.Quantities ?? 0) + quantity
                        );
                        summary.TotalNetValueBeforeDiscount = this.toFixed(
                            (summary.TotalNetValueBeforeDiscount ?? 0) + value
                        );

                        summary.DiscountAmount = this.toFixed(
                            (summary.DiscountAmount ?? 0) + ovDiscAmount
                        );
                        summary.TotalNetValue = this.toFixed(
                            (summary.TotalNetValue ?? 0) + netValue
                        );

                        summary.TotalVATAmount = this.toFixed(
                            (summary.TotalVATAmount ?? 0) + vatValue
                        );

                        summary.TotalGrossValue = this.toFixed(
                            summary.TotalVATAmount + summary.TotalNetValue
                        );

                        if ((detail.Quantity ?? 0) != 0) {
                            // Incomes
                            // https://www.aade.gr/sites/default/files/2024-12/myDATA%20API%20Documentation%20v1%20.0.10_official_erp.pdf

                            // We have to group by Category and Type incomes. Then sum NetValue per group.
                            detail.IncomeCategory =
                                //detail.IncomeCategory ??
                                this.getIncomeClassificationCategory(
                                    invoice,
                                    detail
                                ) ?? 'category1_1'; // category1_1 Έσοδα από Πώληση Εμπορευμάτων (+) / (-)
                            detail.IncomeType =
                                //detail.IncomeType ??
                                this.getIncomeClassificationType(
                                    invoice,
                                    detail
                                ) ?? 'E3_561_001'; // E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών

                            detailFormGroup?.controls?.IncomeCategory?.setValue(
                                detail.IncomeCategory,
                                opts
                            );
                            detailFormGroup?.controls?.IncomeType?.setValue(
                                detail.IncomeType,
                                opts
                            );

                            const income =
                                incomesMap.get(
                                    `${detail.IncomeCategory}-${detail.IncomeType}`
                                ) ??
                                ({
                                    InvoiceId: detail?.InvoiceId,
                                    Amount: 0,
                                    Category: detail.IncomeCategory,
                                    Type: detail.IncomeType
                                } as InvoiceIncomeClassificationType);

                            income.Amount =
                                (income.Amount ?? 0) + (netValue ?? 0);

                            incomesMap.set(
                                `${detail.IncomeCategory}-${detail.IncomeType}`,
                                income
                            );
                        }
                    }
                });

            // We need to group details by income category and income type.
            // Set the new incomes records. Note that the backend will delete from the database any existing records before insert the new records.
            this.invoiceFormGroup()?.controls?.Incomes?.clear(opts);
            incomesMap?.forEach((income) => {
                this.invoiceFormGroup()?.controls?.Incomes?.controls?.push(
                    new InvoiceIncomeClassificationTypeFormGroup(income)
                );

                // Εδώ έχουμε ένα παράδοξο... το getRawValue() επιστρέφει τιμή αλλά το this.formGroup?.controls?.Incomes?.value δεν έχει τιμή, οπότε πρέπει να του ορίσουμε τιμή.
                const incomes =
                    (this.invoiceFormGroup()?.controls?.Incomes?.getRawValue() ??
                        []) as InvoiceIncomeClassificationType[];
                this.invoiceFormGroup()?.controls?.Incomes?.patchValue(
                    incomes as any,
                    opts
                );
            });

            this.taxesMap.set(taxesMap);

            const taxesFormControls =
                this.invoiceFormGroup()?.controls?.Taxes?.controls;

            taxesFormControls?.map((formControl) => {
                const vatPercent =
                    formControl.controls.VATPercent?.getRawValue();

                const tax = this.taxesMap().get(vatPercent?.toString() ?? '');
                formControl.controls.Amount?.setValue(tax?.Amount, opts);
                formControl.controls.Value?.setValue(tax?.Value, opts);
            });

            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.Quantities?.setValue(
                summary.Quantities,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalNetValueBeforeDiscount?.setValue(
                summary.TotalNetValueBeforeDiscount,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.DiscountAmount?.setValue(
                summary.DiscountAmount,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalNetValue?.setValue(
                summary.TotalNetValue,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalVATAmount?.setValue(
                summary.TotalVATAmount,
                opts
            );
            this.invoiceFormGroup()?.controls?.Summaries?.controls[0].controls.TotalGrossValue?.setValue(
                summary.TotalGrossValue,
                opts
            );

            const incomeFormGroup =
                this.invoiceFormGroup()?.controls.Incomes?.controls[0];
            if (incomeFormGroup != undefined) {
                incomeFormGroup.controls.Amount?.setValue(
                    summary.TotalNetValue,
                    opts
                );
            }
        }
    }

    /***  */

    /**
     * Με βάση το Flow Που έχει επιλεγεί (Προ Φόρων ή Μετά Φόρων) και ανάλογα τις αξίες που έχει το προϊον στα πεδία UnitValueRetail
     * και UnitValueRetailAfterTaxes επιστρέφει τη δομή IInvoiceDetailValues
     */
    getDetailValues(
        invoice: InvoiceType,
        detail: InvoiceDetailType,
        summary: InvoiceSummaryType,
        useRetailAfterTaxesEntryFlowForDetailRecord: boolean
    ) {
        /**
         * Η Ποσότητα που ζητείται προς πώληση
         */
        const quantity = detail.Quantity ?? 0; // 6 items
        const discount = detail.Disc ?? 0; // 10%

        /**
         * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους).
         * ΠΡΟΣΟΧΗ: Το πεδίο detail.UnitValue παίρνει τιμή και από το σημείο που ο χρήστης ορίζει αξία μονάδας μέσω του πεδίου UnitValueUser.
         */
        let unitValue = detail.UnitValue ?? 0; // 5 Euro
        let unitValueUser = unitValue;

        // Ζητείται 'Προ Φόρων' αξία μονάδος Flow?
        if (useRetailAfterTaxesEntryFlowForDetailRecord != true) {
            // Ναι.
            // Έχει αξία στο 'Προ Φόρων' πεδίο του προϊόντος?
            if (this.hasUnitValueRetailFieldOfProductValue(detail)) {
                // Τότε δώσε σαν unitValue αυτή τη τιμή.
                unitValue = detail.UnitValueRetail ?? 0;
                if (
                    detail.UnitValueUser != undefined &&
                    detail.UnitValueRetail != detail.UnitValueUser
                )
                    unitValue = detail.UnitValueUser;

                unitValueUser = unitValue;
            } else {
                // Έχει τιμή το 'Μετά Φόρων' Πέδιο του προϊόντος?
                if (
                    this.hasUnitValueRetailAfterTaxesFieldOfProductValue(
                        detail
                    ) == true
                ) {
                    //detail.UnitValueRetailAfterTaxes  != undefined) {
                    // Υπολόγισε αξία μονάδος με αφαίρεση Φ.Π.Α από τη τιμή λιανικής και όρισε unitValue βάση αυτού του υπολογισμού.
                    return this.calculateValuesAsRetailWithTaxes(
                        invoice,
                        detail
                    );
                }
            }
        } else {
            // Ζητείται 'Μετά Φόρων' αξία μονάδος Flow?
            // Ναι.
            // Έχει τιμή το 'Μετά Φόρων' Πέδιο του προϊόντος?
            if (
                this.hasUnitValueRetailAfterTaxesFieldOfProductValue(detail) ==
                true
            ) {
                //detail.UnitValueRetailAfterTaxes  != undefined) {
                // Υπολόγισε αξία μονάδος με αφαίρεση Φ.Π.Α από τη τιμή λιανικής και όρισε unitValue βάση αυτού του υπολογισμού.
                return this.calculateValuesAsRetailWithTaxes(invoice, detail);
            } else {
                // Έχει αξία στο 'Προ Φόρων' πεδίο του προϊόντος?
                if (this.hasUnitValueRetailFieldOfProductValue(detail)) {
                    // Τότε δώσε σαν unitValue αυτή τη τιμή.
                    unitValue = detail.UnitValueRetail ?? 0;
                    if (
                        detail.UnitValueUser != undefined &&
                        detail.UnitValueRetail != detail.UnitValueUser
                    )
                        unitValue = detail.UnitValueUser;

                    unitValueUser = unitValue;
                }
            }
        }

        /**
         * Το σύνολο της Αρχικής Αξία Πώλησης (είναι η ποσότητα * Αρχική Αξία Πώλησης της Μονάδας). Είναι Αξια πριν το Φ.Π.Α.
         */
        let totalNetValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros

        let discAmount = this.toFixed(totalNetValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros

        /**
         * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
         */
        let value = this.toFixed(totalNetValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros
        let ovDiscAmount = this.toFixed(value * (summary.DiscountPercent ?? 0));

        /**
         * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α
         */
        let netValue = this.toFixed(value - ovDiscAmount); //parseFloat((value - ovDiscAmount).toFixed(3)); // 27.00 - 0.81 = 26.19 Euros

        /**
         * Η Αξία του Φ.Π.Α (Υπολογίζεται επί του netValue)
         */
        let VATValue = this.toFixed(netValue * (detail.VATPercent ?? 0));

        // // Ζητείται προ Φόρων αξία μονάδος?
        // if (useRetailAfterTaxesEntryFlowForDetailRecord != true) {
        //     // Έχει αξία στο 'Προ Φόρων' πεδίο του προϊόντος?
        //     if (this.hasUnitValueRetailFieldOfProductValue(detail) != true) {
        //         // Όχι, δεν έχει.
        //         // Έχει τιμή το Μετά Φόρων Πέδιο του προϊόντος?
        //         if (this.hasUnitValueRetailAfterTaxesFieldOfProductValue(detail) == true) {//detail.UnitValueRetailAfterTaxes  != undefined) {
        //             // Υπολόγισε αξία μονάδος με αφαίρεση Φ.Π.Α από τη τιμή λιανικής και όρισε unitValue βάση αυτού του υπολογισμού.
        //             return this.calculateValuesAsRetailWithTaxes(invoice, detail);
        //         }
        //     }
        // }

        // // Εδώ είμαστε πάντα σε Flow 'Προ Φόρων'.
        //  // Έχει αξία στο 'Προ Φόρων' πεδίο του προϊόντος?
        //     if (this.hasUnitValueRetailFieldOfProductValue(detail) != true) {
        //         // Όχι, δεν έχει.
        //         // Έχει τιμή το Μετά Φόρων Πέδιο του προϊόντος?
        //         if (this.hasUnitValueRetailAfterTaxesFieldOfProductValue(detail) == true) {//detail.UnitValueRetailAfterTaxes  != undefined) {
        //             // Υπολόγισε αξία μονάδος με αφαίρεση Φ.Π.Α από τη τιμή λιανικής και όρισε unitValue βάση αυτού του υπολογισμού.
        //             return this.calculateValuesAsRetailWithTaxes(invoice, detail);
        //         }
        //     }

        return {
            /**
             * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους)
             */
            unitValue: this.toFixed(unitValue),
            /**
             * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α και μετά τις εκπτώσεις (της γραμμής και τηνεπί του τιμολογίου)
             */
            netValue: netValue,
            /**
             * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
             */
            value: value,

            /**
             * Η Αξία του Φ.Π.Α (Υπολογίζεται επί του netValue)
             */
            vatValue: VATValue,

            /**
             * Η Ποσότητα που ζητείται προς πώληση
             */
            quantity: quantity,

            /**
             * Το Ποσοστό της έκπτωσης που ζητείται
             */
            discount: discount,

            discAmount: discAmount,

            ovDiscAmount: ovDiscAmount,
            UnitValueUser: unitValueUser,
            NetValueUser: netValue // εδώ θα δείξουμε σοτν χρήστη την αξία πριν το Φ.Π.Α με την έκπτωση της γραμμής χωρίς όμως τη συνολική έκπτωση επί του τιμολογίου
        } as IInvoiceDetailValues;
    }
    /**
     * Επιστρέφει τον τρόπο υπολογισμού της Αξίας Μονάδος.
     * Επιστρέφει αν το flow που θα ακολουθήσουμε για τον υπλογισμό αξίας μονάδος
     * θα είναι αυτό του Προ Φόρων ή Μετά Φόρων. Το επόμενο Step από αυτό το αποτέλεσμα είναι να
     * δούμε αν το προϊόν συμβαδίζει με αυτο το αποτέλεσμα.
     * True: Χρήση Μετά Φόρων,
     * False: Χρήση Προ Φόρων,
     *
     */
    useRetailAfterTaxesEntryFlowForDetailRecord(
        invoice: InvoiceType,
        detail: InvoiceDetailType
    ) {
        switch (detail.UnitValueRetailType) {
            case 'I':
                return invoice.UseRetailAfterTaxes ?? false;
            case 'B':
                return false;
            case 'A':
                return true;
        }

        return false;
    }

    /**
     * Το πεδίο UnitValueRetail του προϊόντος έχει τιμή?
     */
    hasUnitValueRetailFieldOfProductValue(detail: InvoiceDetailType) {
        return (detail.UnitValueRetail ?? 0) != 0;
    }

    /**
     * Το πεδίο UnitValueRetailAfterTaxes του προϊόντος έχει τιμή?
     */
    hasUnitValueRetailAfterTaxesFieldOfProductValue(detail: InvoiceDetailType) {
        return (detail.UnitValueRetailAfterTaxes ?? 0) != 0;
    }

    /**
     * Υπολόγισε αξία μονάδος με αφαίρεση Φ.Π.Α από τη τιμή λιανικής και όρισε unitValue (και τις άλλες αξίες) βάση αυτού του υπολογισμού.
     */
    calculateValuesAsRetailWithTaxes(
        invoice: InvoiceType,
        detail: InvoiceDetailType
    ) {
        const opts = { emitEvent: false };

        const vatValue =
            detail.VATPercent ??
            detail?.ProductPackage?.ProductDepartment?.VATCategory?.Value ??
            0;

        // Αν ο χρήστης ζητάει αξίες μετά φόρων
        // τότε πρέπει να υπολογίσουμε όλες τις αξίες εδώ.
        // Η calculateIncomesAndTaxesWithRetailValues θα κληθεί για να υπολογίσει μόνο τα σύνολα χωρίς υπολογισμούς Φ.Π.Α και χωρίς υπολογισμούς εκπτώσεων
        //if (invoice.UseRetailAfterTaxes == true) {}
        const quantity = detail.Quantity ?? 0; // 7 items
        const discount = detail.Disc ?? 0; // 10%
        let unitValue = detail.UnitValueRetailAfterTaxes ?? 0; //  //retailPriceAfterTaxes ?? 0; // 40 Euro  // detail.UnitValue?.setValue(retailPriceAfterTaxes ?? 0);
        if (
            detail.UnitValueUser != undefined &&
            detail.UnitValueRetailAfterTaxes != detail.UnitValueUser
        )
            unitValue = detail.UnitValueUser;
        const unitValueUser = unitValue;
        //detail.UnitValueUser

        // εδώ έχουμε το σύνολο της λιανικής. Δηλαδή πουλάμε 5 Πορτοκαλάδες * 3.00 (Λιανική έκαστος) = 15.00 Ευρώ
        let totalGrossValue = this.toFixed(quantity * unitValue); // parseFloat((quantity * unitValue).toFixed(2)); // 6 * 5 = 30 Euros
        const discAmount = this.toFixed(totalGrossValue * discount); //parseFloat((totalNetValue * discount).toFixed(2)); // 30.00 * 10% = 3 Euros
        const value = this.toFixed(totalGrossValue - discAmount); // parseFloat((totalNetValue - discAmount).toFixed(2)); // 30.00 - 3.00 = 27 Euros

        // detail.DiscAmount?.setValue(discAmount, opts);
        // detail.Value?.setValue(value, opts);

        const summary = invoice.Summaries?.[0];
        const ovDiscAmount = this.toFixed(
            value * (summary?.DiscountPercent ?? 0)
        );

        // detail.DiscOVAmount?.setValue(ovDiscAmount, opts);

        // Εδώ έχουμε τη τελική αξία πώλησης (μετά φόρων με τις εκπτώσεις της)
        // 7 Τεμ * 40 Ευρώ = 280 Ευρώ
        totalGrossValue = this.toFixed(value - ovDiscAmount);

        // Τώρα να υπολογίσουμε το Φ.Π.Α για αυτή τη τιμή
        // Αφαιρούμε απο την τελική αξία πώλησης το ποσοστό του Φ.Π.Α
        // 280 Ευρώ / 1.24 = 225.81 (225.8064516129)
        const retailPriceWithoutTAX = parseFloat(
            (totalGrossValue / (vatValue + 1)).toFixed(2)
        );

        // Υπολογίζουμε την αξία του Φ.Π.Α αφαιρόντας από την τελική αξία πώλησης την αξία πώλησης χωρίς Φ.Π.Α που υπολογίσαμε πριν.
        // 280 Ευρώ - 225.81 Ευρώ = 54.19  <- Αυτό είναι η αξία του Φ.Π.Α
        const VATValue = parseFloat(
            (totalGrossValue - retailPriceWithoutTAX).toFixed(2)
        );

        // detail.VATAmount?.setValue(VATValue, opts);

        // Υπολογίζουμε εκ νέου τη καθαρή αρχική αξία
        // 280.00 Ευρώ - 54.19 Ευρώ = 225.81 Ευρώ
        const netValue = parseFloat((totalGrossValue - VATValue).toFixed(2)); // 23.99 - 4.64 = 19.35

        //const kk: InvoiceDetailType | undefined = null;

        // detail.NetValue?.setValue(totalNetValue, opts);
        // detail.NetValueWithoutTaxes?.setValue(totalNetValue, opts);

        // // Εδώ ορίζουμε τη αξία μονάδας διαιρώντας το σύνολο με τη ποσότητα
        // // Φυσικά θα υπάρχουνπεριπτώσεις που δεν θα βγαίνει ακριβώς αλλά δεν γίνεται διαφορετικα΄.
        // detail.UnitValue?.setValue(
        //     this.toFixed(totalNetValue / quantity),
        //     opts
        // );

        return {
            /**
             * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους)
             */
            unitValue: this.toFixed(netValue / quantity),
            /**
             * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α και μετά τις εκπτώσεις (της γραμμής και τηνεπί του τιμολογίου)
             */
            netValue: netValue,
            /**
             * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
             */
            value: value,

            /**
             * Η Αξία του Φ.Π.Α (Υπολογίζεται επί του netValue)
             */
            vatValue: VATValue,

            /**
             * Η Ποσότητα που ζητείται προς πώληση
             */
            quantity: quantity,

            /**
             * Το Ποσοστό της έκπτωσης που ζητείται
             */
            discount: discount,

            discAmount: discAmount,

            ovDiscAmount: ovDiscAmount,
            UnitValueUser: unitValueUser,
            NetValueUser: value // Εδώ δείχνουμε την τελική αξία μείον την έκπτωση της γραμμής χωρίς την έκπτωση επι του τιμολοηίου.
        } as IInvoiceDetailValues;
    }

    /***  */
    invoiceDetailKindChange(
        kindType: IInvoiceDetailKindType | undefined,
        rowIndex: number
    ) {
        this.invoiceFormGroup()?.controls.Details?.controls?.[
            rowIndex
        ]?.controls.Kind?.setValue(kindType?._id);
    }

    productUnitChanged(
        productUnitType: ProductUnitTypeType | undefined,
        rowIndex: number
    ) {
        debugger;

        // const productUnitType = this.productsUnitsTypes()?.find(
        //     (productUnitType) => productUnitType._id == _id
        // );
        const mUnit = new ProductUnitTypeTypeFormGroup(
            productUnitType,
            ProductUnitTypeTypeFormGroupSelector.Selector()
        );

        if (productUnitType) {
            this.invoiceFormGroup()?.controls.Details?.controls?.[
                rowIndex
            ]?.controls.MUnit?.setValue(undefined, { emitEvent: false }); // mUnit as any);

            this.invoiceFormGroup()?.controls.Details?.controls?.[
                rowIndex
            ]?.controls.MUnitDescr?.setValue(
                productUnitType.Description ?? productUnitType.Title
            );
        }
    }

    VATPercentChange(
        vatCategory: VATCategoryType | undefined,
        rowIndex: number
    ) {
        this.invoiceFormGroup()?.controls.Details?.controls?.[
            rowIndex
        ]?.controls.VATCategory?.setValue(vatCategory as any);

        this.invoiceFormGroup()?.controls.Details?.controls?.[
            rowIndex
        ]?.controls.VATCategoryId?.setValue(vatCategory?._id);

        this.invoiceFormGroup()?.controls.Details?.controls?.[
            rowIndex
        ]?.controls.VATPercent?.setValue(vatCategory?.Value);
    }

    discountPercentChanged(discountPercent: number) {
        this.invoiceFormGroup()?.controls.Summaries?.controls?.[0]?.controls?.DiscountPercent?.setValue(
            discountPercent
        );
    }

    UnitValueUserChange(rowIndex: number, unitValueUser: number) {
        debugger;

        if (
            this.invoiceFormGroup()?.controls?.Details?.controls?.[rowIndex]
                ?.controls?.UnitValue?.value === unitValueUser
        )
            return;

        // Εδώ ενημε΄ρωνουμε το πεδίο UnitValue γιατί αυτό το πεδίο χρησιμοποιεί η calculateIncomesAndTaxes -> getDetailsvalue() όταν έρχεται η ώρα να υπολογίσει τεελικές αξίες.
        this.invoiceFormGroup()?.controls?.Details?.controls?.[
            rowIndex
        ]?.controls?.UnitValue?.setValue(unitValueUser);
    }

    onCustomerSelected(event: {
        customerType: CustomerType | undefined;
        rowIndex?: number;
    }) {
        this.selectedCustomer.set(event.customerType);

        const opts = { emitEvent: false };

        const customerAddress = event.customerType?.Addresses?.[0];
        const customer = event.customerType;

        this.invoiceFormGroup('services')?.patchValue({
            CustomerId: customer?._id,
            Customer: event.customerType as any,
            SupplierId: undefined,
            IssuerType: undefined,
            CounterPart: {
                Code: customer?.Code,
                Country: 88,
                CountryISO: 'GR',
                SupplierId: undefined,
                CustomerId: customer?._id,
                CustomerStamp: customer?.Stamp,
                Name:
                    customer?.BrandName ??
                    customer?.BrandName ??
                    customer?.FirstName?.Name,
                Occupation: customer?.OccupationActivity?.Title,
                Phone: customer?.ContactDetails?.[0]?.Phone,
                TAXOffice: customer?.CTAXOffice?.Title,
                VATNumber: customer?.TaxId,
                Type: 'C',
                Address: {
                    CityEx: customerAddress?.City?.Title,
                    PostalCode: customerAddress?.PostalCode,
                    StreetName: customerAddress?.StreetName,
                    StreetNumber: customerAddress?.StreetNumber
                }
            }
        });

        this.invoiceFormGroup('products')?.patchValue({
            CustomerId: customer?._id,
            Customer: event.customerType as any,
            SupplierId: undefined,
            IssuerType: undefined,
            CounterPart: {
                Code: customer?.Code,
                Country: 88,
                CountryISO: 'GR',
                SupplierId: undefined,
                CustomerId: customer?._id,
                CustomerStamp: customer?.Stamp,
                Name:
                    customer?.BrandName ??
                    customer?.BrandName ??
                    customer?.FirstName?.Name,
                Occupation: customer?.OccupationActivity?.Title,
                Phone: customer?.ContactDetails?.[0]?.Phone,
                TAXOffice: customer?.CTAXOffice?.Title,
                VATNumber: customer?.TaxId,
                Type: 'C',
                Address: {
                    CityEx: customerAddress?.City?.Title,
                    PostalCode: customerAddress?.PostalCode,
                    StreetName: customerAddress?.StreetName,
                    StreetNumber: customerAddress?.StreetNumber
                }
            }
        });

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.CityEx?.setValue(
        //     undefined,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.PostalCode?.setValue(
        //     undefined,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.StreetName?.setValue(
        //     undefined,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.StreetNumber?.setValue(
        //     undefined,
        //     opts
        // );

        // if (customerAddress != undefined) {
        //     this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.CityEx?.setValue(
        //         customerAddress.City?.Title,
        //         opts
        //     );
        //     this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.PostalCode?.setValue(
        //         customerAddress.PostalCode,
        //         opts
        //     );
        //     this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.StreetName?.setValue(
        //         customerAddress.StreetName,
        //         opts
        //     );
        //     this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Address?.controls?.StreetNumber?.setValue(
        //         customerAddress.StreetNumber,
        //         opts
        //     );
        // }

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Code?.setValue(
        //     customer?.Code,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Country?.setValue(
        //     88, // 88 == GR στο api της Novus //το 87 είναι στο API της Α.Α.Δ.Ε ,
        //     opts
        // ); // Αυτό το θέλει η ΑΑΔΕ
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.CountryISO?.setValue(
        //     'GR',
        //     opts
        // ); // Αυτό το θέλει η ΑΑΔΕ

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.CustomerId?.setValue(
        //     customer?._id,
        //     opts
        // );

        // this.invoiceFormGroup()?.controls?.CustomerId?.setValue(
        //     customer?._id,
        //     opts
        // );

        // // Ensure that Customer Is Selected by setting any previous Supplierid to undefined!!!!
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.SupplierId?.setValue(
        //     undefined,
        //     opts
        // );

        // this.invoiceFormGroup()?.controls?.SupplierId?.setValue(
        //     undefined,
        //     opts
        // );

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.CustomerStamp?.setValue(
        //     customer?.Stamp,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Name?.setValue(
        //     customer?.BrandName ??
        //         customer?.BrandName ??
        //         customer?.FirstName?.Name,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Occupation?.setValue(
        //     customer?.OccupationActivity?.Title,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Phone?.setValue(
        //     customer?.ContactDetails?.[0].Phone,
        //     opts
        // );

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.TAXOffice?.setValue(
        //     customer?.CTAXOffice?.Title,
        //     opts
        // );

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.VATNumber?.setValue(
        //     customer?.TaxId,
        //     opts
        // );
        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?.Type?.setValue(
        //     'C'
        // );

        // this.invoiceFormGroup()?.controls?.CounterPart?.controls?._id?.setValue(
        //     customer._id
        // );

        //this.invoiceFormGroup()?.updateValueAndValidity();
        this.cd.detectChanges();
    }

    toFixed(value: number, fractionDigits: number = 2) {
        const strValue = value
            .toLocaleString('en-US', {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits
            })
            .replace(/\,/g, '');

        if (parseFloat(strValue) != Math.round(value * 100) / 100) {
            // if (parseFloat(strValue) != Math.round(value * 100) / 100) {
            console.warn(
                'mismatched value ',
                value,
                strValue,
                parseFloat(strValue),
                Math.round(value * 100) / 100
            );
        }

        return parseFloat(strValue);
    }

    /**
     * Get the Income Classification Category for invoice and detail.
     * Κατά το ΕΓΛΣ παρ.2.2.2 υποπερ.2.2.200 εμπορευμα ονομάζουμε το υλικό αγαθό που αγοράζεται-αποκτάται απο την οικονομική μονάδα με σκοπό την μεταπώλησή του στην ιδια κατάσταση που αγοράσθηκε.
     * Samples:
     * category1_1 Έσοδα από Πώληση Εμπορευμάτων (+) / (-)
     * category1_2 Έσοδα από Πώληση Προϊόντων
     * category1_3 Έσοδα από Παροχή Υπηρεσιών
     * 
     * Τι ισχύει για ένα συνεργείο αυτοκινήτων:
        Όταν παρέχεις υπηρεσίες επισκευής και χρεώνεις ανταλλακτικά, η πράξη θεωρείται μία ενιαία παροχή υπηρεσίας, εφόσον:

        Τα ανταλλακτικά τοποθετούνται από το συνεργείο (δηλαδή δεν τα παίρνει ο πελάτης ξεχωριστά)

        Και είναι παρεπόμενα της υπηρεσίας (χρειάζονται για να ολοκληρωθεί η επισκευή)

        🔹 Άρα, εκδίδεις μία Απόδειξη Παροχής Υπηρεσιών (ΑΠΥ) ή Τιμολόγιο Παροχής Υπηρεσιών, αν ο πελάτης είναι επαγγελματίας.
        Η απόδειξη μπορεί και πρέπει να περιλαμβάνει τόσο τις υπηρεσίες όσο και τα ανταλλακτικά.

        📄 Παράδειγμα Απόδειξης Παροχής Υπηρεσιών:
        Περιγραφή	Ποσό (€)
        Εργασία: Αλλαγή συμπλέκτη	150,00
        Ανταλλακτικό: Συμπλέκτης	200,00
        Ανταλλακτικό: Λάδι	30,00
        Σύνολο	380,00

        ✅ Σημαντικά:
        Δεν χρειάζεται ξεχωριστή Απόδειξη Λιανικής Πώλησης για τα ανταλλακτικά.

        Η ΑΠΥ καλύπτει και την παροχή και την ενσωματωμένη πώληση.

        Φορολογικά θεωρείται παροχή υπηρεσίας με υλικά και όχι εμπορική πώληση αγαθών.

        Στη δήλωση ΦΠΑ, όλο το ποσό πάει στον ΚΑΔ υπηρεσίας, με διαχωρισμό αν απαιτείται σε αγαθά/υπηρεσίες μόνο για πληροφοριακούς λόγους.

        ❌ Μόνο αν:
        Πουλήσεις τα ανταλλακτικά χωρίς να τα τοποθετήσεις, τότε χρειάζεται Απόδειξη Λιανικής Πώλησης (ΑΛΠ) ή Τιμολόγιο Πώλησης.

        📚 Νομοθετική Βάση:
        Άρθρα 5 & 7 του Κώδικα Φορολογικής Απεικόνισης Συναλλαγών (ΚΦΑΣ) (Ν. 4093/2012)

        Εγκύκλιοι ΠΟΛ και οδηγίες της ΑΑΔΕ για παρεπόμενες παροχές και μικτές συναλλαγές
     */
    getIncomeClassificationCategory(
        invoice: InvoiceType,
        detail: InvoiceDetailType
    ) {
        // TODO: Αλλαγή γιατί όταν πρόκειται για Απόδειξη Πώλησης Εμπορευμάτων / Προϊόντων
        // δεν μπορείς να συμπεριλάβεις και Υπηρεσίες.
        switch (invoice.TypeCode) {
            case '1.1': // Τιμολόγιο Πώλησης Εμπορευμάτων
            case '1.1-9.3': // Τιμολόγιο Πώλησης Εμπορευμάτων - Δελτίο Διακίνησης
                // TODO: Στα τιμολόγια πώλησης Εμπορευμάτων πρέπει να ενεργοποιούμε και το flag Διακίνηση.
                // Εδώ δεν μπορείς να έχει υπηρεσία
                return 'category1_1'; // category1_1: Έσοδα από Πώληση Εμπορευμάτων

            case '2.1': // Τιμολόγιο Παροχής Υπηρεσιών - Τιμολόγιο Παροχής
                //  Αν εδώ πουλάμε και ανταλλακτικά ως μέρος των υπηρεσιών και όχι ανεξάρτητα τότε το έσοδο είναι category1_1: Έσοδα από Πώληση Εμπορευμάτων.
                if (detail.ProductPackage?.ProductDepartment?.IsService != true)
                    //detail.ProductPackage?.IsService == true)
                    return 'category1_3'; // category1_1: Έσοδα από Πώληση Εμπορευμάτων
                return 'category1_3'; // category1_3: Έσοδα από Παροχή Υπηρεσιών

            // TODO: Το πιστωτικό τιμολόγιο είναι έσοδο ή έξοδο?
            case '5.2': // Πιστωτικό Τιμολόγιο
                //  Αν εδώ πουλάμε και ανταλλακτικά ως μέρος των υπηρεσιών και όχι ανεξάρτητα τότε το έσοδο είναι category1_1: Έσοδα από Πώληση Εμπορευμάτων.
                if (detail.ProductPackage?.ProductDepartment?.IsService != true)
                    //detail.ProductPackage?.IsService == true)
                    return 'category1_1'; // category1_1: Έσοδα από Πώληση Εμπορευμάτων
                return 'category1_3'; // category1_3: Έσοδα από Παροχή Υπηρεσιών

            // Παραστατικά Λιανικής
            case '11.1': // Απόδειξη Λιανικής Πώλησης - ΑΛΠ
                // Εδώ δεν μπορείς να έχει υπηρεσία
                return 'category1_1'; // category1_1: Έσοδα από Πώληση Εμπορευμάτων

            case '11.2': // Απόδειξη Παροχής Υπηρεσιών - ΑΠΥ
                if (detail.ProductPackage?.ProductDepartment?.IsService != true)
                    //detail.ProductPackage?.IsService == true)
                    return 'category1_3'; // category1_1: Έσοδα από Πώληση Εμπορευμάτων
                return 'category1_3'; // category1_3: Έσοδα από Παροχή Υπηρεσιών

            // TODO: Το πιστωτικό Στοιχείο Λιανικής είναι έσοδο ή έξοδο?
            case '11.4': // Πιστωτικό Στοιχείο Λιανικής - ΠΣΛ
                // if (detail.ProductPackage?.ProductDepartment?.IsService)
                //     //detail.ProductPackage?.IsService == true)
                //     return 'category1_3'; // category1_3: Έσοδα από Παροχή Υπηρεσιών
                return 'category1_1'; // category1_1: Έσοδα από Πώληση Εμπορευμάτων

            case '9.3':
                return 'category3'; // category3: Διακίνηση
        }

        return '';
    }

    /**
     * Get the Income Classification Type  for invoice and detail.
     * Κατά το ΕΓΛΣ παρ.2.2.2 υποπερ.2.2.200 εμπορευμα ονομάζουμε το υλικό αγαθό που αγοράζεται-αποκτάται απο την οικονομική μονάδα με σκοπό την μεταπώλησή του στην ιδια κατάσταση που αγοράσθηκε.
     * Samples:
     * E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
     * E3_561_003 Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική Πελατεία
     */
    getIncomeClassificationType(
        invoice: InvoiceType,
        detail: InvoiceDetailType
    ) {
        //  <message>Classification type E3_561_001 is forbidden for Classification category category3 combined with invoice type Item9_3</message>
        switch (invoice.TypeCode) {
            case '1.1': // Τιμολόγιο Πώλησης
                return 'E3_561_001'; //E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
            case '1.1-9.3':
                return 'E3_561_001'; //E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
            case '2.1': // Τιμολόγιο Παροχής Υπηρεσιών - Τιμολόγιο Παροχής
                return 'E3_561_001'; //E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
            case '5.2': // Πιστωτικό Τιμολόγιο
                return 'E3_561_001'; //E3_561_001 Πωλήσεις αγαθών και υπηρεσιών Χονδρικές - Επιτηδευματιών
            case '11.1': // Απόδειξη Λιανικής Πώλησης
                return 'E3_561_003'; //E3_561_003 Πωλήσεις αγαθών και υπηρεσιών Λιανικές - Ιδιωτική Πελατεία
            case '11.2': // Παραστατικά Λιανικής - ΑΠΥ
                return 'E3_561_003';
            case '11.4': // Πιστωτικό Στοιχείο Λιανικής
                return 'E3_561_003';
            case '9.3':
                return '';
        }

        return '';
    }

    /**
     * Ensure Last Empty Row and return the invoiceFormGroup
     */
    ensureLastEmptyRecord(
        tabId?: 'services' | 'products' | 'none' | string,
        forceNewRecord?: 'forceNewRecord'
    ) {
        debugger;
        const lastItemIndex =
            (this.invoiceFormGroup(tabId)?.controls?.Details ?? []).length - 1;

        const lastRecord =
            this.invoiceFormGroup(tabId)?.controls?.Details?.controls?.[
                lastItemIndex
            ];

        // Εάν η τελευταία εγγραφή έχει κωδικό τότε πρόσθεσε μια νέα εγγραφή.
        if (
            (lastRecord?.controls?.Code?.value?.trim() ?? '') != '' ||
            (lastRecord?.controls?.Descr?.value?.trim() ?? '') != '' ||
            forceNewRecord == 'forceNewRecord'
        ) {
            debugger;

            const kk = this.invoiceFormGroup(tabId)?.controls.Details
                ?.value as InvoiceDetailType;

            const vatCategory = this.vatCategories()?.find(
                (vatCategory) => vatCategory.Code == 1
            );

            const productUnit = this.productsUnitsTypes()?.[0];

            const detailRowFormGroup = new InvoiceDetailTypeFormGroup(
                {
                    Quantity: 1,
                    UnitTypeId: productUnit?._id,
                    UnitType: productUnit,
                    MUnit: undefined,
                    MUnitDescr: productUnit?.Description ?? productUnit?.Title,
                    VATPercent: vatCategory?.Value,
                    VATCategory: vatCategory,
                    VATCategoryId: vatCategory?._id,
                    Kind:
                        (tabId ?? this.selectedInvoiceTab()) == 'services'
                            ? 'S'
                            : 'M'
                } as InvoiceDetailType,
                InvoiceDetailTypeFormGroupSelector.Selector({
                    VATCategory: VATCategoryTypeFormGroupSelector.Selector(),
                    UnitType: ProductUnitTypeTypeFormGroupSelector.Selector(),
                    ProductPackage:
                        ProductPackageTypeFormGroupSelector.Selector()
                })
            );

            if ((tabId ?? this.selectedInvoiceTab()) == 'products')
                detailRowFormGroup.controls.Kind?.disable();

            const invoice = this.invoiceFormGroup(
                tabId
            ).getRawValue() as InvoiceType;

            // Αν έχει εκδοθεί παραστατικό δεν προσθέτουμε νέα κενή εγγραφή
            if (invoice.Response?.InvoiceMark == undefined)
                this.invoiceFormGroup(tabId)?.controls.Details?.push(
                    detailRowFormGroup
                );

            this.cd.detectChanges();
        }

        return this.invoiceFormGroup(tabId);
    }

    // updateValueAndValidity() {
    //     // this.invoiceFormGroup()?.markAsDirty();
    //     // this.invoiceFormGroup()?.markAsTouched();
    //     this.invoiceFormGroup()?.controls?.CounterPart?.updateValueAndValidity();
    //     //this.invoiceFormGroup()?.updateValueAndValidity();
    //     this.cd.detectChanges();

    //     //this.invoiceFormGroup()?.updateValueAndValidity();
    // }

    // /**
    //  * Fetch Data For the Modal to add/edit/view/delete
    //  * @param formModeType
    //  * @param invoice
    //  */
    // fetchDataById(formModeType: EntityFormModeType, invoice: InvoiceType) {
    //     debugger;

    //     switch (formModeType) {
    //         case 'add':
    //             debugger;
    //             this.invoices[0] = invoice;
    //             this.invoices[1] = invoice;
    //             this.invoice = invoice; //.set(invoice);
    //             //this.visibleDialog = true;
    //             break;
    //         case 'view':
    //         case 'edit':
    //         case 'delete':
    //             //this.loaderService.showAwaitLoader();
    //             return this.store
    //                 .dispatch(
    //                     new InvoiceGraphQLSchemaActions.Fetch(
    //                         queryForEdit,
    //                         QueryFilter.FetchQueryParamsForEntityId(
    //                             invoice?._id
    //                         )
    //                     )
    //                 )
    //                 .pipe(
    //                     //finalize(() => this.loaderService.dismissAwaitLoader())
    //                     concatMap(() => {
    //                         const invoice = this.store.selectSnapshot(
    //                             InvoiceGraphQLSchemaSelectors.FetchSelector(
    //                                 '',
    //                                 ''
    //                             )
    //                         )?.data?.Fetch?.[0];

    //                         //this.loaderService.dismissAwaitLoader();
    //                         this.invoice = { ...invoice };
    //                         return of(this.invoice);
    //                     })
    //                 );
    //             // .subscribe(() => {

    //             //   this.visibleDialog = true;
    //             // });

    //             break;
    //     }

    //     return of(invoice);
    // }

    // /**
    //  * @obsolete
    //  */
    // onInvoiceSave1(statusCode?: string, sendToAAADE?: boolean) {
    //     this.loaderService.showAwaitDataSaveLoader();

    //     const formRawValue = {
    //         ...(this.invoiceFormGroup()?.getRawValue() as InvoiceType)
    //     };

    //     timer(100)
    //         .pipe(
    //             map(() => {
    //                 this.store
    //                     .dispatch(
    //                         new InvoiceGraphQLSchemaActions.Upsert(
    //                             formRawValue,
    //                             undefined,
    //                             queryForEdit
    //                         )
    //                     )
    //                     .pipe(
    //                         concatMap((value) => {
    //                             this.invoiceFormGroup()?.markAsPristine();
    //                             this.invoiceFormGroup()?.markAsUntouched();

    //                             // Refresh Datagrid
    //                             const invoiceUpsertResponse =
    //                                 this.store.selectSnapshot(
    //                                     InvoiceGraphQLSchemaSelectors.UpsertSelector(
    //                                         '',
    //                                         ''
    //                                     )
    //                                 );
    //                             const invoice =
    //                                 invoiceUpsertResponse?.data.Upsert;

    //                             return invoice != undefined
    //                                 ? this.fetchDataById('edit', invoice)
    //                                 : of(undefined);
    //                         }),
    //                         finalize(() => {
    //                             this.loaderService.dismissAwaitLoader();
    //                         })
    //                     )
    //                     .subscribe((invoice) => {
    //                         // Update Data Grid
    //                         //if (invoice) this.rowsToUpdate.set([invoice]);
    //                     });
    //             })
    //         )
    //         .subscribe();
    // }

    /**
     * Do this becuase the UI expects at least a Customer with  at least one ContactDetail, one Notes, one Summary, one income e.t.c
     */
    ensureFormGroupValidArrays(invoice: InvoiceType) {
        //const dialogOptions = this.dialogConfig as InvoiceOptions;
        //if (invoice == undefined) return;
        // Do this becuase the UI expects at least a Customer with  at least one ContactDetail, one Notes, one Summary, one income e.t.c

        invoice.Customer =
            invoice.Customer ??
            ({
                ContactDetails: [{} as ContactDetailType]
            } as CustomerType);

        if ((invoice.Details ?? []).length == 0) {
            invoice.Details = [];
        }

        if ((invoice.Notes ?? []).length == 0) invoice.Notes = undefined;

        invoice.Notes = invoice.Notes ?? [{} as InvoiceNoteType];

        if ((invoice.Summaries ?? []).length == 0)
            invoice.Summaries = undefined;

        invoice.Summaries = invoice.Summaries ?? [{} as InvoiceSummaryType];

        if ((invoice.Incomes ?? []).length == 0) invoice.Incomes = undefined;

        invoice.Incomes = invoice.Incomes ?? [];

        if ((invoice.Payments ?? []).length == 0)
            invoice.Payments = [
                {
                    Amount: 0,
                    CustomerId: undefined,
                    Type: undefined // dialogOptions.sourceModal == true ? '3' : undefined
                } as InvoicePaymentType
            ];

        return invoice;
    }

    getGrandTotal() {
        let grandTotal = 0;
        const invoice = this.invoiceFormGroup()?.getRawValue() as InvoiceType;

        invoice.Details?.map((detail) => {
            if (detail.Deleted != true)
                grandTotal += (detail.NetValue ?? 0) + (detail.VATAmount ?? 0);
            return detail;
        });

        return grandTotal;
    }
}

const queryForEdit: InvoiceTypeQuery = {
    ...InvoiceTypeQuery.GQLQuery(),
    UsersActions: UsersActionsTypeQuery.GQLQuery(),
    CounterPart: InvoicePartyTypeQuery.GQLQuery({
        ...InvoicePartyTypeQuery.GQLQuery(),
        Address: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        }),
        Customer: CustomerTypeQuery.GQLQuery({
            ...CustomerTypeQuery.GQLQuery(),
            FirstName: FirstNameTypeQuery.GQLQuery()
        }),
        Supplier: SupplierTypeQuery.GQLQuery({
            ...CustomerTypeQuery.GQLQuery(),
            FirstName: FirstNameTypeQuery.GQLQuery(),
            Addresses: AddressTypeQuery.GQLQuery({
                City: CityTypeQuery.GQLQuery()
            })
        })
    }),
    Issuer: InvoicePartyTypeQuery.GQLQuery({
        ...InvoicePartyTypeQuery.GQLQuery(),
        Address: AddressTypeQuery.GQLQuery({}),
        Customer: CustomerTypeQuery.GQLQuery({
            ...CustomerTypeQuery.GQLQuery(),
            FirstName: FirstNameTypeQuery.GQLQuery()
        }),
        Supplier: SupplierTypeQuery.GQLQuery({
            ...CustomerTypeQuery.GQLQuery(),
            FirstName: FirstNameTypeQuery.GQLQuery()
        })
    }),
    Courier: CourierTypeQuery.GQLQuery(),
    Customer: CustomerTypeQuery.GQLQuery({
        ...CustomerTypeQuery.GQLQuery(),
        FirstName: FirstNameTypeQuery.GQLQuery(),
        Addresses: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        }),
        ContactDetails: ContactDetailTypeQuery.GQLQuery(),
        Courier: CourierTypeQuery.GQLQuery(),
        DicsountCategory: DiscountCategoryTypeQuery.GQLQuery(),
        CTAXOffice: TAXOfficeTypeQuery.GQLQuery()
    }),
    Supplier: SupplierTypeQuery.GQLQuery({
        Addresses: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        }),
        ContactDetails: ContactDetailTypeQuery.GQLQuery(),
        FirstName: FirstNameTypeQuery.GQLQuery()
    }),
    DeliveryNodeHeader: InvoiceDeliveryNoteHeaderTypeQuery.GQLQuery({
        DeliveryAddress: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        }),
        LoadingAddress: AddressTypeQuery.GQLQuery({
            City: CityTypeQuery.GQLQuery()
        })
    }),
    Details: InvoiceDetailTypeQuery.GQLQuery({
        ProductPackage: ProductPackageTypeQuery.GQLQuery({
            InventoriesList: ProductInventoryInfoTypeQuery.GQLQuery(),
            InvoicingList: ProductInvoicingInfoTypeQuery.GQLQuery()
        })
    }),
    Incomes: InvoiceIncomeClassificationTypeQuery.GQLQuery(),
    Notes: InvoiceNoteTypeQuery.GQLQuery(),
    Response: ProviderResponseTypeQuery.GQLQuery({
        Errors: ResponseTypeErrorTypeQuery.GQLQuery({
            Error: ProviderResponseErrorTypeQuery.GQLQuery()
        }),
        Signatures: SignatureInvoiceResponseTypeQuery.GQLQuery({
            Signatures: ProviderSignatureTypeQuery.GQLQuery({
                SignatureMoreData: ProviderSignatureMoreDataTypeQuery.GQLQuery()
            })
        })
    }),
    DCLCorrelationResponse: DCLResponseTypeQuery.GQLQuery({
        Errors: ResponseTypeErrorTypeQuery.GQLQuery({
            Error: ProviderResponseErrorTypeQuery.GQLQuery()
        })
    }),
    POSTransactions: POSTerminalTransactionTypeQuery.GQLQuery({
        UsersActions: UsersActionsTypeQuery.GQLQuery(),
        InvoicePayment: InvoicePaymentTypeQuery.GQLQuery(),
        TransactionData: POSTerminalTransactionDataTypeQuery.GQLQuery(),
        ServiceData: POSTerminalServiceDataTypeQuery.GQLQuery(),

        Responses: POSTerminalResponseTypeQuery.GQLQuery({
            TransactionData: POSTerminalTransactionDataTypeQuery.GQLQuery(),
            Error: POSTerminalErrorDataTypeQuery.GQLQuery()
        })
    }),

    //Responses: ProviderResponseTypeQuery.GQLQuery(),
    SubjectsInfos: InvoiceEntitySubjectInfoTypeQuery.GQLQuery(),
    Summaries: InvoiceSummaryTypeQuery.GQLQuery(),
    Taxes: InvoiceTaxTypeQuery.GQLQuery(),
    Payments: InvoicePaymentTypeQuery.GQLQuery(),
    DigitalClientCard: DigitalClientCardTypeQuery.GQLQuery({
        Garage: DCLGarageUseTypeQuery.GQLQuery(),
        ParkingUse: DCLParkingCarWashUseTypeQuery.GQLQuery(),
        RentalUse: DCLRentalUseTypeQuery.GQLQuery()
    })
};

const invoiceFormGroupSelector = InvoiceTypeFormGroupSelector.Selector({
    UsersActions: {
        CreatedDate: true
    },
    Customer: CustomerTypeFormGroupSelector.Selector({
        ContactDetails: ContactDetailTypeFormGroupSelector.Selector({
            Customer: CustomerTypeFormGroupSelector.Selector()
        }),
        CTAXOffice: TAXOfficeTypeFormGroupSelector.Selector()
    }),
    CounterPart: InvoicePartyTypeFormGroupSelector.Selector({
        Address: AddressTypeFormGroupSelector.Selector()
    }),
    Details: InvoiceDetailTypeFormGroupSelector.Selector({
        ProductPackage: ProductPackageTypeFormGroupSelector.Selector()
    }),
    Summaries: InvoiceSummaryTypeFormGroupSelector.Selector(),
    Incomes: InvoiceIncomeClassificationTypeFormGroupSelector.Selector(),
    Notes: InvoiceNoteTypeFormGroupSelector.Selector(),
    Response: ProviderResponseTypeFormGroupSelector.Selector({
        Errors: ResponseTypeErrorTypeFormGroupSelector.Selector({
            Error: ProviderResponseErrorTypeFormGroupSelector.Selector()
        }),
        Signatures: SignatureInvoiceResponseTypeFormGroupSelector.Selector({
            Signatures: ProviderSignatureTypeFormGroupSelector.Selector({
                SignatureMoreData:
                    ProviderSignatureMoreDataTypeFormGroupSelector.Selector()
            })
        })
    }),
    DCLCorrelationResponse: DCLResponseTypeFormGroupSelector.Selector({
        Errors: ResponseTypeErrorTypeFormGroupSelector.Selector({
            Error: ProviderResponseErrorTypeFormGroupSelector.Selector()
        })
    }),
    POSTransactions: POSTerminalTransactionTypeFormGroupSelector.Selector({
        UsersActions: {
            CreatedDate: true,
            CreatedByUserId: true
        },
        InvoicePayment: InvoicePaymentTypeFormGroupSelector.Selector(),
        TransactionData:
            POSTerminalTransactionDataTypeFormGroupSelector.Selector(),
        ServiceData: POSTerminalServiceDataTypeFormGroupSelector.Selector(),
        Responses: POSTerminalResponseTypeFormGroupSelector.Selector({
            TransactionData:
                POSTerminalTransactionDataTypeFormGroupSelector.Selector(),
            Error: POSTerminalErrorDataTypeFormGroupSelector.Selector()
        })
    }),
    Taxes: InvoiceTaxTypeFormGroupSelector.Selector(),
    Payments: InvoicePaymentTypeFormGroupSelector.Selector()
});

export interface IInvoiceDetailValues {
    /**
     * Η Αρχική Αξία πώλησης της Μονάδας (Αυτό είναι Πριν τις εκπτώσεις και πριν τους φόρους)
     */
    unitValue: number;

    /**
     * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α και μετά τις εκπτώσεις (της γραμμής και την επί του τιμολογίου)
     */
    netValue: number;

    /**
     * Το Σύνολο της Αρχικής Αξίας πώλησης μείον την έκπτωση (Αφορά την έκπτωση της γραμμής όχι τη συνολική). Είναι Αξια πριν το Φ.Π.Α.
     */
    value: number;
    /**
     * Η Αξία του Φ.Π.Α (Υπολογίζεται επί του netValue)
     */
    vatValue: number;

    /**
     * Η Ποσότητα που ζητείται προς πώληση
     */
    quantity: number;

    /**
     * Το Ποσοστό της έκπτωσης που ζητείται
     */
    discount?: number;

    discAmount?: number;

    ovDiscAmount?: number;

    /**
     * Η τιμή που βλέπει ο χρήστης στο UI.
     * ΠΡΟΣΟΧΗ: Αν είναι σε προ φόρων αξίες τότε αυτο είναι ίσο με το unitValue δηλαδή αξία πριν Φ.Π.Α και πριν τις εκπτώσεις (π.χ πώληση υπηρεσίας 150 Ευρώ, αυτό είναι αρχική αξία).
     *          Αν είναι μετά φόρων αξίες τότε αυτό είναι ίσο με τη τελική αξία πώλησης δηλαδή με το Φ.Π.Α αλλά χωρίς τις εκπτώσεις (π.χ πώληση 1 κιλό ψωμί με 1 ευρώ. Οι εκπτώσεις θα μπουν μετά).
     */
    UnitValueUser?: number;

    /**
     * Η τελική αξία πώλησης του προϊόντος πριν το Φ.Π.Α και μετά τις εκπτώσεις (της γραμμής και όχι την επί του τιμολογίου) όπως πρέπει να το βλέπει ο χρήστης στο UI ανάλογα την επιλεγμένη αξία (προ φόρων ή μετά φόρων)
     * Στη μεταφόρων στο πεδίο αυτό πρέπει να έχουμε τη τελική τιμή - τις εκπτώσεις.
     */
    NetValueUser?: number;
}
